import React, { useState, useRef, useEffect } from 'react';
import {
    MoreHorizontal,
    Plus,
    ChevronDown,
    ChevronUp,
    X,
    SquareArrowOutUpRight,
} from 'lucide-react';
import { useHistory, useLocation } from "react-router-dom";
import noBrandImage from '../../assets/img/brand/no-brand-img.png';
import nolistingImage from '../../assets/img/brand/noImage.png';
import emptySearchImg from '../../assets/img/brand/empty-search.png';
import Pagination from '../../components/Pagination';
// Kept while the single-box search is commented out below (ERP-5233).
// eslint-disable-next-line no-unused-vars
import SearchBox from '../../components/SearchBox';
import ListingSearchBars from '../../components/ListingSearchBars';
import FilterDrawer from '../../components/FilterDrawer';
import { formatDateTime } from '../../utils/formateDate';
import { isPlaceholderStock } from '../../utils/placeholderStock';
import { getContentColumnWidth, measureTextWidth } from '../../utils/columnWidth';
import ConfirmDeactivateModal from '../../components/ConfirmDeactivateModal';
import apiHelper from '../../apiHelper';
import { API_ENDPOINT } from '../../config';   // [Notes debug] remove with the logs below
import AlertNotification from '../../components/AlertNotification';
import LoadingNotification from '../../components/LoadingNotification';
import ConfirmArchiveModal from '../../components/ConfirmArchiveModal';
import { useLoader } from '../../components/GlobalLoader';
import StockDrawer from '../../components/Stockdrawer';
import SkuInfoDrawer from '../../components/SkuInfoDrawer';
import StockTags from '../../components/StockTags';

const SET_TYPE_OPTIONS = ['Primary', 'Telco', 'None'];

const getPlatformStatusInfo = (lastSyncStatus) => {
    // success/completed/synced -> green; every other case (failed, error,
    // pending, processing, no data, unknown) -> red.
    // 'locked' is a Lazada campaign price lock: the mapping is connected and
    // syncing, only the ERP override is parked until the campaign ends, so it
    // is green too (with a Campaign tag next to the name).
    // ERP-5170: 'success_delayed' is what retry_lazada_locked_prices leaves once
    // a campaign lifts and the parked price goes out — a success, and by then
    // not even a parked one, so no Campaign tag either.
    if (lastSyncStatus && ['success', 'completed', 'synced', 'locked', 'success_delayed'].includes(lastSyncStatus.toLowerCase())) {
        return { dotColor: '#146C43', textColor: '#212529', status: 'success' };
    }
    return { dotColor: '#B02A37', textColor: '#58151C', status: 'failed' };
};

const PAGE_SIZE = 10;

// "Campaign" tag shown next to a Lazada platform whose price is locked by a
// running campaign (last_sync_status 'locked'). Same shape as the Delist pill,
// amber per Figma. Lazada gives no campaign name or dates through its API, so
// the tag can only say *that* a campaign is on, not which one or until when.
const CAMPAIGN_TAG_TITLE = 'Under Lazada campaign — the platform price is frozen. ERP price edits are kept and pushed to Lazada once the campaign ends (retried daily 1:00pm SGT).';
const CAMPAIGN_TAG_STYLE = { display: 'flex', padding: '2px 5px', justifyContent: 'center', alignItems: 'center', gap: 10, borderRadius: 99, border: '1px solid #CC9A06', background: '#FFF3CD', color: '#CC9A06', textAlign: 'center', fontFamily: 'Montserrat', fontSize: 8, fontWeight: 600, lineHeight: 'normal', flexShrink: 0 };
// Drawer variant: taller, wider, same colours.
const CAMPAIGN_TAG_STYLE_LG = { ...CAMPAIGN_TAG_STYLE, height: 20, minWidth: 72, padding: '2px 10px', fontSize: 10 };
// "Delist" pill. Same shape as the Campaign tag, red per Figma. Lifted out of
// the two cells that render it so the Platforms column can measure its width
// (see PLATFORM_TAG_FONT / platformTagWidth below).
// Committed line under In Stock — Figma ERP-5184, Body/Extra Small: Montserrat 10px / 400 / normal, #6C757D (--Utilised-text-2).
const COMMITTED_STYLE = { color: '#6C757D', fontFamily: 'Montserrat', fontSize: 10, fontStyle: 'normal', fontWeight: 400, lineHeight: 'normal', marginTop: 2, whiteSpace: 'nowrap' };
const DELIST_TAG_STYLE = { display: 'flex', padding: '2px 5px', justifyContent: 'center', alignItems: 'center', gap: 10, borderRadius: 99, border: '1px solid #B02A37', background: '#F8D7DA', color: '#B02A37', textAlign: 'center', fontFamily: 'Montserrat', fontSize: 8, fontWeight: 600, lineHeight: 'normal', flexShrink: 0 };

// Room the Platforms cell needs beyond the platform name itself.
//
// The cell is  [8px dot] 8px gap [name] (8px gap [tag])*  inside a td with 10px
// of padding each side. Both tags are flexShrink:0, so when the column is too
// narrow it is the *name* that ellipsises and the tag that gets clipped by the
// cell's overflow:hidden - which is what turned "WooCommerce SG" + Delist into
// "WooComm..." with half a pill. The column therefore has to be measured, the
// same way Platform Price already is.
const PLATFORM_TAG_FONT = '600 8px Montserrat, sans-serif';
const PLATFORM_DOT_SPACE = 8 + 8;   // dot + its gap
const PLATFORM_TAG_GAP = 8;
const PLATFORM_TAG_CHROME = 5 + 5 + 1 + 1;   // padding + border, each side
const platformTagWidth = (label) =>
    Math.ceil(measureTextWidth(label, PLATFORM_TAG_FONT)) + PLATFORM_TAG_CHROME;

function formatPrice(value) {
    if (!value) return '-';
    const m = String(value).replace(/[^\d.-]/g, '');
    if (m === '') return '-';
    const num = Number(m.replace(/,/g, ''));
    if (isNaN(num)) return '-';
    return num.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const FilterActiveIcon = () => (
    <svg style={{ margin: 0 }} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20.6325 6.44444C20.7252 6.24267 20.7746 6.02858 20.8107 5.74756C21.0354 4.00089 21.1478 3.12756 21.6356 2.56378C20.1234 2 19.2177 2 17.4063 2H4.59369C2.78227 2 1.87655 2 1.36439 2.56378C0.852232 3.12756 0.964572 4.00089 1.18925 5.74756C1.25487 6.25766 1.36399 6.54725 1.69704 6.94296C2.77293 8.22128 4.74334 10.4951 7.50829 12.5623C7.76095 12.7512 7.92676 13.0599 7.95475 13.4016C8.26623 17.2071 8.55159 19.8068 8.70309 21.0865C8.78256 21.7577 9.52673 22.274 10.14 21.8404C11.1702 21.1118 13.0947 20.2952 13.349 18.938C13.4692 18.2964 13.6451 17.1765 13.8437 15.3333" stroke="#0D6EFD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <g clipPath="url(#clip0_plt_a)">
            <path d="M22.8125 11C22.8125 14.2102 20.2102 16.8125 17 16.8125C13.7898 16.8125 11.1875 14.2102 11.1875 11C11.1875 7.78987 13.7898 5.18753 17 5.18753C20.2102 5.18753 22.8125 7.78987 22.8125 11ZM16.3277 14.0777L20.6402 9.7652C20.7866 9.61877 20.7866 9.38132 20.6402 9.23488L20.1099 8.70456C19.9634 9.5581 19.726 9.5581 19.5795 8.70456L16.0625 12.2215L14.4205 10.5795C14.2741 10.4331 14.0366 10.4331 13.8901 10.5795L13.3598 11.1099C13.2134 11.2563 13.2134 11.4937 13.3598 11.6402L15.7973 14.0777C15.9438 14.2241 16.1812 14.2241 16.3277 14.0777Z" fill="#0D6EFD" />
        </g>
        <defs>
            <clipPath id="clip0_plt_a">
                <rect width="12" height="12" fill="white" transform="translate(11 5)" />
            </clipPath>
        </defs>
    </svg>
);

const FilterInactiveIcon = () => (
    <svg style={{ margin: 0 }} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M21.6323 6.44444C21.7251 6.24267 21.7744 6.02858 21.8106 5.74756C22.0353 4.00089 22.1476 3.12756 21.6354 2.56378C21.1233 2 20.2176 2 18.4062 2H5.59366C3.78225 2 2.87655 2 2.36439 2.56378C1.85223 3.12756 1.96457 4.00089 2.18925 5.74756C2.25487 6.25766 2.36399 6.54725 2.69703 6.94296C3.77291 8.22128 5.74331 10.4951 8.50823 12.5623C8.76089 12.7512 8.9267 13.0599 8.95469 13.4016C9.26616 17.2071 9.55152 19.8068 9.70302 21.0865C9.78249 21.7577 10.5267 22.274 11.1399 21.8404C12.1701 21.1118 14.0946 20.2952 14.3489 18.938C14.4691 18.2964 14.645 17.1765 14.8436 15.3333" stroke="#6C757D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M18.1113 7.5556V15.3334M22.0001 11.4445L14.2224 11.4445" stroke="#6C757D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const FilterDisabledIcon = () => (
    <svg style={{ margin: 0 }} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M21.6323 6.44444C21.7251 6.24267 21.7744 6.02858 21.8106 5.74756C22.0353 4.00089 22.1476 3.12756 21.6354 2.56378C21.1233 2 20.2176 2 18.4062 2H5.59366C3.78225 2 2.87655 2 2.36439 2.56378C1.85223 3.12756 1.96457 4.00089 2.18925 5.74756C2.25487 6.25766 2.36399 6.54725 2.69703 6.94296C3.77291 8.22128 5.74331 10.4951 8.50823 12.5623C8.76089 12.7512 8.9267 13.0599 8.95469 13.4016C9.26616 17.2071 9.55152 19.8068 9.70302 21.0865C9.78249 21.7577 10.5267 22.274 11.1399 21.8404C12.1701 21.1118 14.0946 20.2952 14.3489 18.938C14.4691 18.2964 14.645 17.1765 14.8436 15.3333" stroke="#CED4DA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M18.1113 7.5556V15.3334M22.0001 11.4445L14.2224 11.4445" stroke="#CED4DA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// Maps the Stock Qty Consistency filter labels to backend query values.
const CONSISTENCY_PARAM_MAP = { 'Consistent Stock Qty': 'consistent', 'Inconsistent Stock Qty': 'inconsistent' };
const mapConsistencyValue = (v) => CONSISTENCY_PARAM_MAP[v] || '';

// Maps the Platform Stock filter labels to the stock_sync_option values the API stores.
const STOCK_SYNC_PARAM_MAP = { 'Sync ERP Qty': 'sync_erp', 'Set to 0': 'sync_to_0' };
const mapStockSyncValue = (v) => STOCK_SYNC_PARAM_MAP[v] || '';

// True when a platform's synced stock quantity differs from the ERP qty a
// sync_erp mapping pushes: In Stock MINUS committed (ERP-5184, Jess 29 Sep —
// In Stock 13 with one open order pushes 12, so 12 on the platform is
// consistent). Callers pass that figure via erpAvailableQty below.
// Only compares platforms that actually carry a synced stock value (not pending /
// failed / never-synced), matching what the table shows for Platform Stock.
// A 0 qty is left as the normal "Sold Out" display (not highlighted).
const isStockInconsistent = (platform, erpQty) =>
    !!platform?.lastSyncStatus && !platform.isPending && !platform.isFailed &&
    platform.platformStock != null && Number(platform.platformStock) !== 0 &&
    erpQty != null &&
    Number(platform.platformStock) !== Number(erpQty);

// In Stock minus committed for the stock row. Committed is the STOCK's figure
// (group.committedQty = every listing of the stock, every platform), because
// that is what the backend subtracts before pushing (sales/utils.py
// erp_available_qty) — a unit sold on Shopee is gone for Lazada too. Falls back
// to the listing's own numbers where no group is in scope. Floored at 0.
const erpAvailableQty = (group, listing) => {
    const inStock = group?.inStock ?? listing?.inStock;
    if (inStock == null) return null;
    const committed = group?.committedQty ?? listing?.committedQty ?? 0;
    return Math.max(Number(inStock) - Number(committed), 0);
};

// Highlighted Platform Stock badge shown when ERP In Stock ≠ Platform Stock.
// Renders the qty in a gold pill with an info icon; the tooltip is position:fixed
// (like the In Stock branch popup) so it is not clipped by the cell's overflow.
function StockInconsistencyBadge({ children }) {
    const [tipPos, setTipPos] = useState(null);
    const showTip = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setTipPos({ left: r.left + r.width / 2, bottom: window.innerHeight - r.top + 8 });
    };
    const hideTip = () => setTipPos(null);
    return (
        <span className="plt-incon-wrap">
            <span className="plt-incon-badge">{children}</span>
            <span
                className="plt-incon-info"
                onMouseEnter={showTip}
                onMouseLeave={hideTip}
                onClick={(e) => e.stopPropagation()}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <g clipPath="url(#clip0_plt_incon)">
                        <path fillRule="evenodd" clipRule="evenodd" d="M5 9.375C6.16032 9.375 7.27312 8.91406 8.09359 8.09359C8.91406 7.27312 9.375 6.16032 9.375 5C9.375 3.83968 8.91406 2.72688 8.09359 1.90641C7.27312 1.08594 6.16032 0.625 5 0.625C3.83968 0.625 2.72688 1.08594 1.90641 1.90641C1.08594 2.72688 0.625 3.83968 0.625 5C0.625 6.16032 1.08594 7.27312 1.90641 8.09359C2.72688 8.91406 3.83968 9.375 5 9.375ZM5 10C6.32608 10 7.59785 9.47322 8.53553 8.53553C9.47322 7.59785 10 6.32608 10 5C10 3.67392 9.47322 2.40215 8.53553 1.46447C7.59785 0.526784 6.32608 0 5 0C3.67392 0 2.40215 0.526784 1.46447 1.46447C0.526784 2.40215 0 3.67392 0 5C0 6.32608 0.526784 7.59785 1.46447 8.53553C2.40215 9.47322 3.67392 10 5 10Z" fill="#5E6E82"/>
                        <path d="M5.58122 4.11768L4.14997 4.29705L4.09872 4.53455L4.37997 4.58643C4.56372 4.63018 4.59997 4.69643 4.55997 4.87955L4.09872 7.04705C3.97747 7.60768 4.16435 7.87143 4.60372 7.87143C4.94435 7.87143 5.33997 7.71393 5.51935 7.49768L5.57434 7.23768C5.44934 7.34768 5.26684 7.39143 5.1456 7.39143C4.97372 7.39143 4.91122 7.2708 4.9556 7.0583L5.58122 4.11768Z" fill="#5E6E82"/>
                        <path d="M5 3.4375C5.34518 3.4375 5.625 3.15768 5.625 2.8125C5.625 2.46732 5.34518 2.1875 5 2.1875C4.65482 2.1875 4.375 2.46732 4.375 2.8125C4.375 3.15768 4.65482 3.4375 5 3.4375Z" fill="#5E6E82"/>
                    </g>
                    <defs><clipPath id="clip0_plt_incon"><rect width="10" height="10" fill="white"/></clipPath></defs>
                </svg>
            </span>
            {tipPos && (
                <div
                    className="plt-incon-tip"
                    style={{ left: tipPos.left, bottom: tipPos.bottom, transform: 'translateX(-50%)' }}
                >
                    Stock Qty between ERP and Platform are inconsistent. Platform shall prevail
                </div>
            )}
        </span>
    );
}

// Lazada-only info icon + tooltip explaining the struck-through Normal Price
// shown next to a Special Price (Platform Price) that is below it.
function NormalPriceInfo() {
    const [tipPos, setTipPos] = useState(null);
    const showTip = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        // Anchor the tooltip's top-right corner at the icon, so the box opens
        // down and to the left of it.
        setTipPos({ right: window.innerWidth - r.right, top: r.bottom + 8 });
    };
    const hideTip = () => setTipPos(null);
    return (
        <span
            className="plt-normprice-info"
            onMouseEnter={showTip}
            onMouseLeave={hideTip}
            onClick={(e) => e.stopPropagation()}
        >
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 15 15" fill="none">
                <g clipPath="url(#clip0_normprice)">
                    <path d="M7.5 10V7.5M7.5 5H7.50625M13.75 7.5C13.75 10.9518 10.9518 13.75 7.5 13.75C4.04822 13.75 1.25 10.9518 1.25 7.5C1.25 4.04822 4.04822 1.25 7.5 1.25C10.9518 1.25 13.75 4.04822 13.75 7.5Z" stroke="#666666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </g>
                <defs><clipPath id="clip0_normprice"><rect width="15" height="15" fill="white"/></clipPath></defs>
            </svg>
            {tipPos && (
                <div
                    className="plt-normprice-tip"
                    style={{ right: tipPos.right, top: tipPos.top }}
                >
                    <div className="plt-normprice-tip-title">Normal Price</div>
                    <div>The normal price that retrieve from lazada</div>
                </div>
            )}
        </span>
    );
}

const isLazadaPlatform = (name) => (name || '').toLowerCase().includes('lazada');

// "SGD 500.00" plus, for a Lazada row whose Special Price is below its Normal
// (list) price, a struck-through Normal Price and an info icon explaining it.
// The text a platform row puts in the Platform Price cell: the price, plus -
// for a Lazada row - its Normal (list) price whenever Lazada gave us one, even
// when the two are equal (ERP-5162: the stakeholder wants the Normal Price
// visible in every case, not only when there is a discount).
// One place, because the column is also SIZED from these strings (see the
// colWidths effect in the component).
function platformPriceParts(p) {
    const cur = p.currencyCode || 'SGD';
    const priceText = `${cur} ${formatPrice(p.platformPrice)}`;
    const showNormal = isLazadaPlatform(p.name) && p.normalPrice != null;
    return { priceText, normalText: showNormal ? `${cur} ${formatPrice(p.normalPrice)}` : null };
}

// Non-text chrome the Platform Price cell adds around those strings when the
// Normal Price shows: two 4px gaps and the 12px info icon, plus a little slack
// for the space that stands in for the gaps when the strings are measured.
const PLATFORM_PRICE_CELL_EXTRA = 24;

function renderPlatformPriceValue(p) {
    const { priceText, normalText } = platformPriceParts(p);
    if (!normalText) return priceText;
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span>{priceText}</span>
            <span style={{ textDecoration: 'line-through', color: '#666666' }}>
                {normalText}
            </span>
            <NormalPriceInfo />
        </span>
    );
}

// Map raw API listing item to UI shape
function mapListingToUi(item) {
    // [Notes debug] Remove once Notes is confirmed working on stage.
    // hasNotesKey is the decisive signal: the backend only emits `notes` on a
    // platform row if the deployed build has SalesListingMapping.notes. If it is
    // false, the server is running a build without the Notes feature.
    console.log('[Notes debug] listing', item.sales_listing_id, {
        listingLevelNotes: item.notes,
        hasNotesKeyOnListing: 'notes' in item,
        platformRows: (item.platform_data || []).map(pl => ({
            platform: pl.platform_name,
            hasNotesKey: 'notes' in pl,
            notes: pl.notes,
        })),
    });
    const stock = item.stock || {};
    const platforms = (item.platform_data || []).map(platform => {
        const syncStatus = platform.last_sync_status;
        const statusInfo = getPlatformStatusInfo(syncStatus);
        const isFailed = syncStatus?.toLowerCase() === 'failed' || syncStatus?.toLowerCase() === 'error';
        const isPending = !syncStatus || syncStatus?.toLowerCase() === 'pending' || syncStatus?.toLowerCase() === 'processing';
        const isCampaign = syncStatus?.toLowerCase() === 'locked';
        return {
            name: platform.platform_name || 'Unknown Platform',
            platformProductId: platform.platform_product_id,
            platformSkuId: platform.platform_sku_id,
            platformPrice: platform.platform_price ? String(platform.platform_price) : null,
            normalPrice: platform.normal_price != null ? String(platform.normal_price) : null,
            platformStock: platform.platform_stock_quantity != null ? Number(platform.platform_stock_quantity) : null,
            lastSyncTime: platform.last_sync_at ? formatDateTime(platform.last_sync_at) : null,
            lastSyncStatus: syncStatus,
            lastErrorMessage: platform.last_error_message || platform.error_message,
            dotColor: statusInfo.dotColor,
            textColor: statusInfo.textColor,
            currencyCode: platform.platform_currency_code || 'SGD',
            isFailed,
            isPending,
            isCampaign,
            isDelisted: platform.is_delisted || false,
            productName: platform.platform_product_name,
            skuName: platform.platform_sku_name,
            basePrice: platform.base_price ?? null,
            basePriceCurrency: platform.base_price_currency || 'SGD',
            mappingId: platform.mapping_id ?? null,
            stockSyncOption: platform.stock_sync_option ?? null,
            notes: platform.notes || '',
            platformModifiedAt: platform.mapping_modified_at || null,
            platformModifiedBy: platform.mapping_modified_by || null,
            // Units this platform SKU already owes to open orders.
            committedQty: platform.committed_qty ?? 0,
        };
    });

    const inStockSummary = stock.stock_status_summary?.inStock_Sellable || [];
    const inStock = inStockSummary.find(s => s.type === 'In Stock')?.count ?? 0;
    // Server-side total for this listing, already de-duplicated across mappings
    // that share a platform SKU — see ViewListingDetailSerializer.get_committed_qty.
    const committedQty = item.committed_qty ?? 0;

    // Cost price from stock instances (min/max range or single)
    const cpData = item.cost_price_data ?? null;   // {min, max, is_uniform} or null

    return {
        id: item.sales_listing_id,
        stockId: item.stock_id,
        name: stock.spu_name || '-',
        variant: stock.sku_name || '-',
        model_number: stock.model_number || '',
        avatar: stock.sku_image || nolistingImage,
        condition: stock.condition || 'NA',
        grading: stock.condition_type || 'NA',
        setType: stock.set_type || '',
        status: item.status ? 'Active' : 'Inactive',
        listingCode: item.listing_code || '',
        notes: item.notes || '',
        modified_at: item.modified_at || '',
        created_at: item.created_at || '',
        created_by: item.created_by || '',
        modified_by: item.modified_by || '',
        inStock,
        committedQty,
        costPriceData: cpData,   // {min, max, is_uniform} | null
        basePrice: null,
        platforms,
    };
}

// ERP-5171: the placeholder stock is the catch-all for platform listings with no
// real stock behind them, so it collects far more mappings than a real stock and its
// parent row would stack one line per mapping and run off the screen. Its Platforms /
// Platform Stock / Platform Price columns stop after four lines and end in an
// ellipsis; Expand still lists every mapping as a sub-row.
const PLACEHOLDER_PARENT_ROW_LIMIT = 4;

// Stands in for the mappings the parent row stopped short of, on its own line so the
// three capped columns keep lining up with each other.
const MoreMappingsEllipsis = () => (
    <div style={{ fontSize: 12, color: '#6C757D', lineHeight: 'normal' }}>…</div>
);

// Group listings by stock_id. A placeholder-stock listing is never grouped with
// another — many listings share that stock legitimately, and tying them into one row
// also costs them their action menu, which only a single-listing row carries.
function groupByStock(listings) {
    const map = new Map();
    for (const l of listings) {
        const key = isPlaceholderStock(l.name, l.variant) ? `l${l.id}` : `s${l.stockId}`;
        if (!map.has(key)) {
            map.set(key, {
                key,
                stockId: l.stockId,
                name: l.name,
                variant: l.variant,
                model_number: l.model_number,
                avatar: l.avatar,
                condition: l.condition,
                grading: l.grading,
                setType: l.setType,
                inStock: l.inStock,
                // Summed over the row's listings as they are added below. Each
                // listing's own figure is already de-duplicated server-side, so
                // the only way to double count here is two listings of the SAME
                // stock carrying the SAME platform SKU — which would be the
                // duplicate-mapping data problem, not a display bug.
                committedQty: 0,
                costPriceData: l.costPriceData,   // {min, max, is_uniform} | null
                listings: [],
            });
        }
        map.get(key).committedQty += (l.committedQty || 0);
        map.get(key).listings.push(l);
    }
    return Array.from(map.values());
}

// What the top banner says for each row action, in flight and once it settles.
// These actions used to resolve in silence — a failure only reached
// console.error — so the "done" and "failed" copy is new.
const ACTION_LABELS = {
    delist:   { progress: 'Delisting 1 Listing',      done: 'Delisted Successfully',              failed: 'Failed to Delist' },
    list:     { progress: 'Relisting 1 Listing',      done: 'Relisted Successfully',              failed: 'Failed to Relist' },
    sold_out: { progress: 'Updating 1 Listing Stock', done: 'Platform Stock Set to 0',            failed: 'Failed to Update Platform Stock' },
    restore:  { progress: 'Updating 1 Listing Stock', done: 'Platform Stock Synced to ERP Stock', failed: 'Failed to Update Platform Stock' },
};

export default function PlatformListingTest() {
    const breadcrumbs = ['Listing', 'Platform Listing'];
    const history = useHistory();
    const location = useLocation();
    const { showLoader, hideLoader } = useLoader();

    const [listings, setListings] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    // Still here for the commented-out SearchBox above.
    // eslint-disable-next-line no-unused-vars
    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    // ERP-5233 two-box search. `*Input` is what is being typed; the un-prefixed
    // name is the committed term (Enter, a picked suggestion, or an emptied box),
    // and `debounced*` is what actually reaches the API — same three stages the
    // single box used, so paging and the prefetch cache behave identically.
    const [modelSearchInput, setModelSearchInput] = useState('');
    // The committed model term travels with the flag that says how to match it.
    // `exact` is set only when the term came out of the typeahead, where every
    // entry is a model name that exists — picking "iPhone 15" there means iPhone
    // 15, not iPhone 15 Pro. Typing your own words and pressing Enter stays broad.
    const [modelSearch, setModelSearch] = useState({ term: '', exact: false });
    const [debouncedModelSearch, setDebouncedModelSearch] = useState({ term: '', exact: false });
    // Refetches after the first paint dim the table instead of covering the screen.
    const [tableLoading, setTableLoading] = useState(false);
    const isFirstLoad = useRef(true);
    const loadSeq = useRef(0);
    const [detailSearchInput, setDetailSearchInput] = useState('');
    const [detailSearch, setDetailSearch] = useState('');
    const [debouncedDetailSearch, setDebouncedDetailSearch] = useState('');
    const [sortField, setSortField] = useState('modified_at');
    const [sortOrder, setSortOrder] = useState('desc');
    const [filterPopupOpen, setFilterPopupOpen] = useState(false);
    const [filters, setFilters] = useState({ status: [], platforms: [], stockSync: [], consistency: [], setTypes: [] });
    const [tempValues, setTempValues] = useState({ status: [], platforms: [], stockSync: [], consistency: [], setTypes: [] });
    const [allPlatforms, setAllPlatforms] = useState([]);

    // Fetch full platform list for the filter (same source as Add/Edit Platform Listing)
    useEffect(() => {
        apiHelper('sales/platforms/', 'GET').then(async res => {
            if (res.ok) {
                const d = await res.json();
                setAllPlatforms(Array.isArray(d) ? d.map(p => p.platform_name).filter(Boolean) : []);
            }
        }).catch(() => {});
    }, []);
    const [appliedFilter, setAppliedFilter] = useState(null);
    const [expandedStocks, setExpandedStocks] = useState({});
    const [successMessage, setSuccessMessage] = useState('');
    const [refreshTick, setRefreshTick] = useState(0);

    const [dropdownOpenId, setDropdownOpenId] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const dropdownRef = useRef(null);
    const buttonRefs = useRef({});
    const filterButtonRef = useRef(null);

    const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
    const [selectedListing, setSelectedListing] = useState(null);
    const [archivedModalOpen, setArchivedModalOpen] = useState(false);
    const [selectedListingId, setSelectedListingId] = useState(null);

    // Drawer state — type: 'listing' | 'platform'
    const [drawer, setDrawer] = useState(null);
    const [drawerMenuOpen, setDrawerMenuOpen] = useState(false);
    const [drawerTab, setDrawerTab] = useState('general');
    const drawerRef = useRef(null);
    const drawerMenuRef = useRef(null);

    const [colWidths, setColWidths] = useState({
        stock: 220,
        inStock: 100,
        costPrice: 140,
        platforms: 160,
        platformStock: 120,
        basePrice: 140,
        platformPrice: 140,
        notes: 150,
    });

    const handleColResizeMouseDown = (e, key, startWidth, minWidth = 80) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const onMouseMove = (moveEvent) => {
            const delta = moveEvent.clientX - startX;
            setColWidths((prev) => ({ ...prev, [key]: Math.max(minWidth, startWidth + delta) }));
        };
        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    };

    // Platform Price follows its longest value on the page. The table is
    // table-layout:fixed and .entities-table td clips with an ellipsis, so at the
    // 140px default a Lazada row's "SGD 3,278.00 SGD 3,279.00 (i)" was cut to
    // "SGD 3,278.00 SGD 32". Widen-only: a narrower drag by the user stands until
    // the next page of data arrives.
    useEffect(() => {
        const needed = parseInt(getContentColumnWidth(
            listings.flatMap(l => l.platforms || []),
            (p) => {
                if (!p.platformPrice) return '';
                const { priceText, normalText } = platformPriceParts(p);
                return normalText ? `${priceText} ${normalText}` : priceText;
            },
            { label: 'Platform Price', extra: PLATFORM_PRICE_CELL_EXTRA, min: 140, max: 480 }
        ), 10);
        setColWidths(prev => (needed > prev.platformPrice ? { ...prev, platformPrice: needed } : prev));
    }, [listings]);

    // Platforms follows its widest [dot][name][tag...] line on the page, for the
    // same reason. Measured row by row rather than through getContentColumnWidth
    // because the chrome is not uniform here — only some rows carry a tag, and a
    // row can carry both.
    useEffect(() => {
        const CELL_PADDING = 20;   // .entities-table td, 10px each side
        let widest = 0;
        listings.forEach((l) => (l.platforms || []).forEach((p) => {
            let w = PLATFORM_DOT_SPACE + measureTextWidth(p.name);
            if (p.isCampaign) w += PLATFORM_TAG_GAP + platformTagWidth('Campaign');
            if (p.isDelisted) w += PLATFORM_TAG_GAP + platformTagWidth('Delist');
            if (w > widest) widest = w;
        }));
        if (!widest) return;
        // +1 for sub-pixel rounding — measureText can land a hair under what the
        // browser paints, which shows up as a stray ellipsis.
        const needed = Math.min(360, Math.max(160, Math.ceil(widest) + CELL_PADDING + 1));
        setColWidths(prev => (needed > prev.platforms ? { ...prev, platforms: needed } : prev));
    }, [listings]);

    const ColResizeHandle = ({ colKey, minWidth }) => (
        <div
            className="col-resize-handle"
            onMouseDown={(e) => handleColResizeMouseDown(e, colKey, colWidths[colKey], minWidth)}
            onClick={(e) => e.stopPropagation()}
            style={{ position: 'absolute', right: 0, top: 0, height: '100%', width: '6px', cursor: 'col-resize', zIndex: 2, userSelect: 'none' }}
        />
    );

    const pltTableScrollRef = useRef(null);
    const [pltScrolledLeft, setPltScrolledLeft] = useState(false);
    const [pltScrolledRight, setPltScrolledRight] = useState(false);
    const handlePltTableScroll = () => {
        const el = pltTableScrollRef.current;
        if (!el) return;
        setPltScrolledLeft(el.scrollLeft > 0);
        setPltScrolledRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };

    // Stock sub-drawer (opened from inside Listing Info drawer)
    const [stockDrawerId, setStockDrawerId] = useState(null);
    const [stockDrawerModelNumber, setStockDrawerModelNumber] = useState(null);
    const [skuDrawerOpen, setSkuDrawerOpen] = useState(false);
    const [selectedSkuRow, setSelectedSkuRow] = useState(null);

    // Branch balance popup
    const [branchPopup, setBranchPopup] = useState(null); // { stockId, data, loading, rect }
    const branchCache = useRef({});
    const branchPopupRef = useRef(null);
    const branchHideTimer = useRef(null);

    const prefetchCache = useRef(new Map());
    const prefetchInProgress = useRef(new Set());
    const isFirstSearchRender = useRef(true);
    const isFirstFilterRender = useRef(true);

    const openBranchPopup = async (e, stockId) => {
        e.stopPropagation();
        clearTimeout(branchHideTimer.current);
        const rect = e.currentTarget.getBoundingClientRect();
        if (branchCache.current[stockId]) {
            setBranchPopup({ stockId, data: branchCache.current[stockId], loading: false, rect });
            return;
        }
        setBranchPopup({ stockId, data: null, loading: true, rect });
        try {
            const res = await apiHelper(`inventory/stocks/${stockId}/branch-balance/`, 'GET');
            if (res.ok) {
                const data = await res.json();
                branchCache.current[stockId] = data;
                setBranchPopup(prev => prev?.stockId === stockId ? { stockId, data, loading: false, rect } : prev);
            } else {
                setBranchPopup(null);
            }
        } catch (_) {
            setBranchPopup(null);
        }
    };

    const closeBranchPopup = () => {
        branchHideTimer.current = setTimeout(() => setBranchPopup(null), 150);
    };

    const keepBranchPopupOpen = () => {
        clearTimeout(branchHideTimer.current);
    };

    // The search half of a request, carried as one object so adding a third box
    // later does not mean widening two function signatures and four call sites.
    //   q            legacy single-box term (still honoured by the backend)
    //   model        left-hand box — the SPU model name, nothing else
    //   modelExact   true when that term was picked from the typeahead
    //   detail       right-hand box — everything except the model name
    function appendSearchParams(params, sq) {
        if (sq?.q?.trim()) params.append('search', sq.q.trim());
        if (sq?.model?.trim()) {
            params.append('model_search', sq.model.trim());
            if (sq.modelExact) params.append('model_search_exact', 'true');
        }
        if (sq?.detail?.trim()) params.append('detail_search', sq.detail.trim());
        return params;
    }

    function buildCacheKey(page, sq, af, sf, so) {
        return JSON.stringify({
            page,
            search: (sq?.q || '').trim(),
            model: (sq?.model || '').trim(),
            // The flag only counts when a term is actually typed, or an empty box
            // would key differently depending on how it was last emptied.
            modelExact: (sq?.model || '').trim() ? !!sq?.modelExact : false,
            detail: (sq?.detail || '').trim(),
            af, sf, so,
        });
    }

    function buildOrdering(sf, so) {
        if (!sf) return '-modified_at';
        return `${so === 'desc' ? '-' : ''}${sf}`;
    }

    async function prefetchPage(page, sq, af, sf, so, totalCnt) {
        const totalPgs = Math.ceil(totalCnt / PAGE_SIZE);
        if (page > totalPgs) return;
        const key = buildCacheKey(page, sq, af, sf, so);
        if (prefetchCache.current.has(key) || prefetchInProgress.current.has(key)) return;
        prefetchInProgress.current.add(key);
        try {
            const params = new URLSearchParams({ page, page_size: PAGE_SIZE });
            appendSearchParams(params, sq);
            if (af?.status?.length) params.append('status', af.status.join(','));
            if (af?.platforms?.length) params.append('platform', af.platforms.join(','));
            if (af?.stockSync?.length) {
                const sv = af.stockSync.map(mapStockSyncValue).filter(Boolean);
                if (sv.length) params.append('stock_sync_option', sv.join(','));
            }
            if (af?.consistency?.length) {
                const cv = af.consistency.map(mapConsistencyValue).filter(Boolean);
                if (cv.length) params.append('stock_qty_consistency', cv.join(','));
            }
            if (af?.setTypes?.length) params.append('set_type', af.setTypes.join(','));
            params.append('ordering', buildOrdering(sf, so));
            const res = await apiHelper(`sales/listings/?${params.toString()}`, 'GET');
            if (!res.ok) return;
            const json = await res.json();
            prefetchCache.current.set(key, {
                results: (json.results || []).map(mapListingToUi),
                count: json.count || 0,
            });
        } catch (_) { } finally {
            prefetchInProgress.current.delete(key);
        }
    }

    // Rebuilt whenever any of the three terms settles; everything below keys off
    // this one value rather than three separate ones.
    // Pulled apart so the fetch effect's dependency array stays statically
    // checkable — a member expression in there trips react-hooks/exhaustive-deps.
    const modelTerm = debouncedModelSearch.term;
    const modelExact = debouncedModelSearch.exact;

    const searchQuery = {
        q: debouncedSearch,
        model: modelTerm,
        modelExact,
        detail: debouncedDetailSearch,
    };

    useEffect(() => {
        async function load() {
            const key = buildCacheKey(currentPage, searchQuery, appliedFilter, sortField, sortOrder);
            const cached = prefetchCache.current.get(key);
            if (cached) {
                setListings(cached.results);
                setTotalCount(cached.count);
                prefetchPage(currentPage + 1, searchQuery, appliedFilter, sortField, sortOrder, cached.count);
                return;
            }
            // The global loader is a full-screen opaque overlay — right for the first
            // paint of the page, far too heavy for a search/sort/filter refetch. Those
            // keep the table on screen and dim it instead.
            const seq = ++loadSeq.current;
            if (isFirstLoad.current) showLoader(); else setTableLoading(true);
            try {
                const params = new URLSearchParams({ page: currentPage, page_size: PAGE_SIZE });
                appendSearchParams(params, searchQuery);
                if (appliedFilter?.status?.length) params.append('status', appliedFilter.status.join(','));
                if (appliedFilter?.platforms?.length) params.append('platform', appliedFilter.platforms.join(','));
                if (appliedFilter?.stockSync?.length) {
                    const sv = appliedFilter.stockSync.map(mapStockSyncValue).filter(Boolean);
                    if (sv.length) params.append('stock_sync_option', sv.join(','));
                }
                if (appliedFilter?.consistency?.length) {
                    const cv = appliedFilter.consistency.map(mapConsistencyValue).filter(Boolean);
                    if (cv.length) params.append('stock_qty_consistency', cv.join(','));
                }
                if (appliedFilter?.setTypes?.length) params.append('set_type', appliedFilter.setTypes.join(','));
                params.append('ordering', buildOrdering(sortField, sortOrder));
                // [Notes debug] Remove once Notes is confirmed working on stage.
                console.log('[Notes debug] GET', `${API_ENDPOINT}/sales/listings/?${params.toString()}`);
                const res = await apiHelper(`sales/listings/?${params.toString()}`, 'GET');
                if (!res.ok) throw new Error('Failed to fetch');
                const json = await res.json();
                console.log('[Notes debug] search=', params.get('search') || '(none)', '| count=', json.count);
                // A slower earlier request must not overwrite a newer one's rows.
                if (seq !== loadSeq.current) return;
                const mapped = (json.results || []).map(mapListingToUi);
                prefetchCache.current.set(key, { results: mapped, count: json.count || 0 });
                setListings(mapped);
                setTotalCount(json.count || 0);
                prefetchPage(currentPage + 1, searchQuery, appliedFilter, sortField, sortOrder, json.count || 0);
            } catch (err) {
                console.error(err);
            } finally {
                if (seq === loadSeq.current) {
                    if (isFirstLoad.current) { isFirstLoad.current = false; hideLoader(); }
                    else setTableLoading(false);
                }
            }
        }
        load();
        return () => { prefetchCache.current.clear(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, debouncedSearch, modelTerm, modelExact, debouncedDetailSearch,
        appliedFilter, sortField, sortOrder, refreshTick]);

    useEffect(() => {
        if (isFirstSearchRender.current) { isFirstSearchRender.current = false; return; }
        const t = setTimeout(() => { setCurrentPage(1); setDebouncedSearch(searchTerm); }, 300);
        return () => clearTimeout(t);
    }, [searchTerm]);

    useEffect(() => {
        const t = setTimeout(() => { setCurrentPage(1); setDebouncedModelSearch(modelSearch); }, 300);
        return () => clearTimeout(t);
    }, [modelSearch]);

    useEffect(() => {
        const t = setTimeout(() => { setCurrentPage(1); setDebouncedDetailSearch(detailSearch); }, 300);
        return () => clearTimeout(t);
    }, [detailSearch]);

    useEffect(() => {
        if (isFirstFilterRender.current) { isFirstFilterRender.current = false; return; }
        setCurrentPage(1);
        prefetchCache.current.clear();
        prefetchInProgress.current.clear();
    }, [appliedFilter, sortField, sortOrder]); // eslint-disable-line

    useEffect(() => {
        if (location?.state?.restoreDrawer) {
            setDrawer(location.state.restoreDrawer);
            history.replace(location.pathname, { ...location.state, restoreDrawer: undefined });
        }
    }, []); // eslint-disable-line

    useEffect(() => {
        if (location?.state?.success) {
            setSuccessMessage(location.state.success);
            history.replace(location.pathname, {});
            const t = setTimeout(() => setSuccessMessage(''), 5000);
            return () => clearTimeout(t);
        }
    }, [location, history]);


    // Close drawer on outside click — moved below pendingUnmap/pendingAction declarations

    // Close drawer ... menu on outside click
    useEffect(() => {
        function handler(e) {
            if (drawerMenuOpen && drawerMenuRef.current && !drawerMenuRef.current.contains(e.target)) {
                setDrawerMenuOpen(false);
            }
        }
        if (drawerMenuOpen) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [drawerMenuOpen]);

    // Close dropdown on outside click
    useEffect(() => {
        function handler(e) {
            if (
                dropdownOpenId !== null &&
                dropdownRef.current &&
                !dropdownRef.current.contains(e.target) &&
                !buttonRefs.current[dropdownOpenId]?.contains(e.target)
            ) {
                setDropdownOpenId(null);
            }
        }
        if (dropdownOpenId !== null) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [dropdownOpenId]);

    // ERP-5241 guard. A per-mapping filter narrows the Platforms / Platform Stock
    // / Platform Price lines inside a row, so a listing can come back with every
    // one of its mappings filtered away and nothing left to show. Drawing that as
    // a stock card reading "0 Listing" with three empty columns is noise — the
    // row matched nothing the user asked for. The server no longer sends those
    // (sales/views.py settles the listing set with mapping_matches_filters before
    // paginating), and this keeps a stale prefetch or a half-deployed backend from
    // putting one back. Only per-mapping narrowing counts: with no filter, or with
    // Set Type alone, an unmapped listing genuinely IS a "0 Listing" row and stays.
    const mappingLevelNarrowing = !!debouncedSearch
        || !!appliedFilter?.status?.length
        || !!appliedFilter?.platforms?.length
        || !!appliedFilter?.stockSync?.length
        || !!appliedFilter?.consistency?.length;
    const visibleListings = mappingLevelNarrowing
        ? listings.filter(l => l.platforms.length > 0)
        : listings;

    const grouped = groupByStock(visibleListings);
    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

    // ERP-5173: edge shadows must be right on first paint, after a column
    // resize and after a viewport resize — not only once the user scrolls.
    useEffect(() => {
        handlePltTableScroll();
        window.addEventListener('resize', handlePltTableScroll);
        return () => window.removeEventListener('resize', handlePltTableScroll);
    }, [grouped, colWidths]);

    const filtersApplied = Object.entries(filters).some(([, v]) => Array.isArray(v) ? v.length > 0 : v !== '');

    const platformOptions = allPlatforms.map(name => ({ title: name, value: name }));

    const filterSections = [
        {
            label: 'API Status',
            type: 'checkbox',
            key: 'status',
            options: ['Connect', 'Disconnect'],
            divider: true,
        },
        {
            label: 'Platform',
            type: 'list',
            key: 'platforms',
            data: platformOptions,
            renderItem: item => ({ id: item.value, title: item.title, subtitle: '', image: null }),
            showDropdownIcon: true,
            optionHoverBg: '#F8F9FA',
            divider: true,
        },
        {
            label: 'Platform Stock',
            type: 'checkbox',
            key: 'stockSync',
            options: ['Sync ERP Qty', 'Set to 0'],
            divider: true,
        },
        {
            label: 'Stock Qty Consistency',
            type: 'checkbox',
            key: 'consistency',
            options: ['Consistent Stock Qty', 'Inconsistent Stock Qty'],
            divider: true,
        },
        // ERP-5160 - the Stock page's Set Type filter widget (chip box + dropdown)
        // over the three values Stock.set_type takes; "None" also covers a blank.
        {
            label: 'Set Type',
            type: 'list',
            key: 'setTypes',
            data: SET_TYPE_OPTIONS,
            renderItem: name => ({ id: name, title: name, subtitle: '', image: null }),
            showDropdownIcon: true,
            optionHoverBg: '#F8F9FA',
        },
    ];

    // Every drawer opens on General, including when switching straight from one
    // listing/platform target to another without closing in between.
    useEffect(() => { setDrawerTab('general'); }, [drawer?.type, drawer?.listing?.id, drawer?.platform?.mappingId]);

    const toggleExpand = (groupKey) => {
        setExpandedStocks(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
    };

    const toggleDropdown = (id) => {
        if (dropdownOpenId === id) { setDropdownOpenId(null); return; }
        const btn = buttonRefs.current[id];
        if (btn) {
            const rect = btn.getBoundingClientRect();
            const w = 170;
            const h = 150;
            let left = rect.left + window.scrollX + rect.width - w;
            if (left < 8) left = 8;
            let top = rect.bottom + window.scrollY;
            if (top + h > window.scrollY + window.innerHeight) top = rect.top + window.scrollY - h;
            setDropdownPosition({ top, left });
        }
        setDropdownOpenId(id);
    };

    const handleDeactivate = async (listingId) => {
        try {
            const res = await apiHelper(`sales/listings/${listingId}/status/`, 'PATCH', { status: false });
            if (!res.ok) return;
            setDeactivateModalOpen(false);
            prefetchCache.current.clear();
            setRefreshTick(t => t + 1);
        } catch (err) { console.error(err); }
    };

    const handleActivate = async (listing) => {
        try {
            const res = await apiHelper(`sales/listings/${listing.id}/status/`, 'PATCH', { status: true });
            if (!res.ok) return;
            prefetchCache.current.clear();
            setRefreshTick(t => t + 1);
        } catch (err) { console.error(err); }
    };

    const handleArchive = async (listingId) => {
        setArchivedModalOpen(false);
        setSelectedListingId(null);
        setDrawer(null);
        setListings(prev => prev.filter(l => l.id !== listingId));
        try {
            const res = await apiHelper(`sales/listings/${listingId}/archive/`, 'PATCH', { is_archived: true });
            if (!res.ok) {
                // Revert on failure — re-fetch
                prefetchCache.current.clear();
                setRefreshTick(t => t + 1);
            } else {
                prefetchCache.current.clear();
            }
        } catch (err) {
            console.error(err);
            prefetchCache.current.clear();
            setRefreshTick(t => t + 1);
        }
    };

    const [stockActionLoading, setStockActionLoading] = useState(null); // mapping_id being acted on
    // Outcome of a row action, shown in the same top slot the progress banner
    // uses. These actions used to resolve in silence — a failure only ever
    // reached console.error — so there was nothing to report at the top.
    const [actionResult, setActionResult] = useState(null); // { type, message }
    // What the progress banner says while the request is in flight. Separate from
    // stockActionLoading because that holds the mapping id the row buttons compare
    // themselves against, and widening it would touch every one of those checks.
    const [stockActionLabel, setStockActionLabel] = useState('');
    const [pendingUnmap, setPendingUnmap] = useState(null); // { listing, platform }
    const [pendingAction, setPendingAction] = useState(null); // { listing, platform, action: 'delist'|'list'|'sold_out'|'restore' }

    const ACTION_MODAL_CONTENT = {
        delist:  { title: 'Confirm to delist this Listing?',                      description: 'Once delisted, this Listing will be delisted from WooCommerce. You may list it again via ERP in the future.' },
        list:    { title: 'Confirm to list this Listing?',                        description: 'Once listed, this Listing It will be published on WooCommerce with the current Platform Price.' },
        sold_out:{ title: 'Confirm to mark platform stock as sold out?',           description: 'Once marked, the stock of this Listing on the corresponding platform will be updated to 0 via API.' },
        restore: { title: 'Confirm to set platform stock to match ERP In Stock?',  description: 'After confirmation, the stock quantity of this Listing on the platform will be updated via API to the same quantity as ERP In Stock.' },
    };

    const requestAction = (listing, platform, action) => {
        setDropdownOpenId(null);
        setDrawerMenuOpen(false);
        setPendingAction({ listing, platform, action });
    };

    const confirmPendingAction = async () => {
        if (!pendingAction) return;
        const { listing, platform, action } = pendingAction;
        setPendingAction(null);
 
        if (action === 'delist' || action === 'list') {
            if (!platform?.mappingId) return;
            setStockActionLoading(platform.mappingId);
            setActionResult(null);
            setStockActionLabel(ACTION_LABELS[action].progress);

            try {
                const res = await apiHelper(
                    `sales/listing-mapping/list-delist/${platform.mappingId}/`,
                    'POST',
                    { action }   // sends { action: 'delist' } or { action: 'list' }
                );
                if (!res.ok) {
                    const err = await res.json().catch(() => ({}));
                    console.error('Delist/List action failed:', err.error || 'Unknown error');
                    setActionResult({ type: 'error', message: ACTION_LABELS[action].failed });
                    return;
                }
                const data = await res.json();
                const updatedPlatformMapper = p =>
                    p.mappingId === platform.mappingId
                        ? { ...p, isDelisted: action === 'delist', ...data }
                        : p;

                setListings(prev => prev.map(l =>
                    l.id !== listing.id ? l : { ...l, platforms: l.platforms.map(updatedPlatformMapper) }
                ));
                setDrawer(prev => {
                    if (!prev || prev.listing?.id !== listing.id) return prev;
                    return {
                        ...prev,
                        platform: prev.platform?.mappingId === platform.mappingId
                            ? updatedPlatformMapper(prev.platform)
                            : prev.platform,
                        listing: {
                            ...prev.listing,
                            platforms: prev.listing.platforms.map(updatedPlatformMapper),
                        },
                    };
                });
                setActionResult({ type: 'success', message: ACTION_LABELS[action].done });
            } catch (e) {
                console.error(e);
                setActionResult({ type: 'error', message: ACTION_LABELS[action].failed });
            } finally {
                setStockActionLoading(null);
            }

            // TODO: wire to Delist/List API when ready
        } else {
            handleStockAction(listing, platform, action);
        }
    };

    const handleUnmapMapping = (listing, platform) => {
        if (!platform?.mappingId) return;
        setDropdownOpenId(null);
        setDrawerMenuOpen(false);
        setPendingUnmap({ listing, platform });
    };

    const confirmUnmapMapping = async () => {
        if (!pendingUnmap) return;
        const { listing, platform } = pendingUnmap;
        setPendingUnmap(null);
        try {
            const res = await apiHelper(`sales/listings/${listing.id}/mappings/${platform.mappingId}/`, 'DELETE');
            if (!res.ok) { console.error('Unmap failed'); setDrawer(null); return; }
            const data = await res.json();
            setListings(prev => prev.map(l => {
                if (l.id !== listing.id) return l;
                return {
                    ...l,
                    platforms: l.platforms.filter(p => p.mappingId !== platform.mappingId),
                    ...(data.modified_at ? { modified_at: data.modified_at } : {}),
                    ...(data.modified_by ? { modified_by: data.modified_by } : {}),
                };
            }));
            // Update the open drawer's listing so modified_at/modified_by reflects immediately
            setDrawer(prev => {
                if (!prev || prev.listing?.id !== listing.id) return null;
                return {
                    ...prev,
                    listing: {
                        ...prev.listing,
                        platforms: prev.listing.platforms.filter(p => p.mappingId !== platform.mappingId),
                        ...(data.modified_at ? { modified_at: data.modified_at } : {}),
                        ...(data.modified_by ? { modified_by: data.modified_by } : {}),
                    },
                };
            });
        } catch (e) {
            console.error(e);
            setDrawer(null);
        }
    };

    // Close drawer on outside click — but NOT while a nested drawer (stock/SKU) or a
    // confirmation modal is stacked on top, so a click/back inside the nested drawer
    // returns to this main drawer instead of dismissing everything.
    useEffect(() => {
        function handler(e) {
            if (stockDrawerId || skuDrawerOpen || archivedModalOpen || pendingUnmap || pendingAction) return;
            if (drawer && drawerRef.current && !drawerRef.current.contains(e.target)) {
                setDrawer(null);
                setDrawerMenuOpen(false);
            }
        }
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [drawer, stockDrawerId, skuDrawerOpen, archivedModalOpen, pendingUnmap, pendingAction]);

    const handleStockAction = async (listing, platform, action) => {
        if (!platform.mappingId) return;
        setStockActionLoading(platform.mappingId);
        setActionResult(null);
        setStockActionLabel((ACTION_LABELS[action] || {}).progress || 'Updating...');
        setDropdownOpenId(null);
        try {
            const res = await apiHelper(
                `sales/listings/${listing.id}/mappings/${platform.mappingId}/update-stock/`,
                'POST',
                { action }
            );
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                console.error('Stock action failed:', err.error || 'Unknown error');
                setActionResult({ type: 'error', message: (ACTION_LABELS[action] || {}).failed || 'Failed to Update Platform Stock' });
                return;
            }
            const data = await res.json();
            const updatedPlatformMapper = p =>
                p.mappingId === platform.mappingId
                    ? {
                        ...p,
                        platformStock: data.new_quantity,
                        stockSyncOption: data.stock_sync_option,
                        platformModifiedAt: data.platform_modified_at || p.platformModifiedAt,
                        platformModifiedBy: data.platform_modified_by || p.platformModifiedBy,
                    }
                    : p;
            // Update listings state: per-platform stock, sync option, and modified fields
            setListings(prev => prev.map(l => {
                if (l.id !== listing.id) return l;
                return { ...l, platforms: l.platforms.map(updatedPlatformMapper) };
            }));
            // Update open drawer: drawer.platform snapshot + listing platforms list
            setDrawer(prev => {
                if (!prev || prev.listing?.id !== listing.id) return prev;
                const updatedDrawerPlatform =
                    prev.platform?.mappingId === platform.mappingId
                        ? updatedPlatformMapper(prev.platform)
                        : prev.platform;
                return {
                    ...prev,
                    platform: updatedDrawerPlatform,
                    listing: {
                        ...prev.listing,
                        platforms: prev.listing.platforms.map(updatedPlatformMapper),
                    },
                };
            });
            setActionResult({ type: 'success', message: (ACTION_LABELS[action] || {}).done || 'Platform Stock Updated' });
        } catch (e) {
            console.error(e);
            setActionResult({ type: 'error', message: (ACTION_LABELS[action] || {}).failed || 'Failed to Update Platform Stock' });
        } finally {
            setStockActionLoading(null);
        }
    };

    // visibleListings, not listings: if the ERP-5241 guard above drops every row
    // the page came back with, the empty-state copy is what belongs there, not a
    // blank tbody. The pagination footer stays keyed to the raw page so the user
    // can still page off such a page.
    const isTableEmpty = visibleListings.length === 0;
    const anySearchActive = !!debouncedSearch || !!modelTerm || !!debouncedDetailSearch;
    const isSearchOrFilterActive = anySearchActive || filtersApplied;
    const isSearchFilterDisabled = isTableEmpty && !isSearchOrFilterActive;

    return (
        <div className="company-body new-look stock-wrap bg-checkbox">
            <style>{`
                .plt-action-btn:focus, .plt-action-btn:active, .plt-action-btn:focus-visible {
                    outline: none !important; box-shadow: none !important;
                }
                .plt-drawer-action-btn {
                    background: none; border: none; cursor: pointer;
                    display: flex; align-items: center; justify-content: center;
                    width: 32px; height: 32px; border-radius: 8px; padding: 0;
                    color: #212529;
                    transition: background 0.15s, box-shadow 0.15s;
                }
                .plt-drawer-action-btn:focus, .plt-drawer-action-btn:focus-visible {
                    outline: none !important;
                }
                .plt-drawer-action-btn:active,
                .plt-drawer-action-btn.active {
                    background: #FFFFFF !important;
                    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.10) !important;
                    border-radius: 8px !important;
                }
                .plt-dropdown-btn:hover { background: #FFFEF0 !important; }

                /* Grip mark on each column's resize handle */
                .col-resize-handle::after {
                    content: "";
                    position: absolute;
                    top: 50%;
                    transform: translateY(-50%);
                    height: 50%;
                    left: 3px;
                    width: 2px;
                    background: #DEE2E6;
                }

                /* Sticky-column edge shadows — only shown while there's
                   actually scrolled content hidden behind the sticky
                   column, and forced to overflow:visible so the shadow
                   can escape despite overflow:hidden used for text
                   truncation on the same cells. */
                .plt-table th.plt-sticky-edge,
                .plt-table td.plt-sticky-edge {
                    overflow: visible !important;
                }
                .plt-table.is-scrolled-left th.plt-sticky-edge::after,
                .plt-table.is-scrolled-left td.plt-sticky-edge::after {
                    content: "";
                    position: absolute;
                    top: 0;
                    right: -8px;
                    width: 8px;
                    height: 100%;
                    pointer-events: none;
                    z-index: 1;
                    background: linear-gradient(to right, rgba(0,0,0,0.12), rgba(0,0,0,0));
                }
                .plt-table.is-scrolled-right th.plt-action-edge::before,
                .plt-table.is-scrolled-right td.plt-action-edge::before {
                    content: "";
                    position: absolute;
                    top: 0;
                    left: -8px;
                    width: 8px;
                    height: 100%;
                    pointer-events: none;
                    z-index: 1;
                    background: linear-gradient(to left, rgba(0,0,0,0.12), rgba(0,0,0,0));
                }
                /* The shadow above sits at left:-8px, outside the cell, so the
                   cell must not clip it. The header already allows overflow
                   inline; the body cells did not, which is why the shadow
                   showed on the header only. */
                .plt-table th.plt-action-edge,
                .plt-table td.plt-action-edge {
                    overflow: visible !important;
                }

                /* ── ERP-5173: sticky edges survive horizontal scroll ──────────────
                   Borders and radii already live on the CELLS (card grouping above), so
                   they travel with the sticky cell. What a cell cannot do is hide the
                   column scrolling underneath it: a rounded corner leaves a notch, and
                   through it the covered cell's square corner and its border line show —
                   the "broken corner + stray line" seen while scrolling. A 9×9 mask in
                   that notch covers it: transparent inside the curve, the border colour
                   on the curve, the table surface outside. Pseudo-elements, so no markup
                   changes: left cells use ::before (::after is the left shadow band),
                   right cells use ::after (::before is the right shadow band).
                   The outside colour is #FFFFFF, not the page grey — the table sits on
                   .table-wrapper's white, and the design's --surface-card is white too. */
                .plt-table thead th:first-child::before,
                .plt-table thead th:last-child::after,
                .plt-table .plt-stock-row td:first-child::before,
                .plt-table .plt-stock-row td:last-child::after,
                .plt-table .plt-group-bottom td:first-child::before,
                .plt-table .plt-group-bottom td:last-child::after {
                  content: "";
                  position: absolute;
                  width: 9px;
                  height: 9px;
                  pointer-events: none;
                  z-index: 2;
                }
                /* Header bar: no border, 8px radius — one strip per side masks both corners */
                .plt-table thead th:first-child::before {
                  top: 0; bottom: 0; left: 0; width: 8px; height: auto;
                  background:
                    radial-gradient(circle at 8px 8px, transparent 0 8px, #FFFFFF 8px) left top / 8px 8px no-repeat,
                    radial-gradient(circle at 8px 0,   transparent 0 8px, #FFFFFF 8px) left bottom / 8px 8px no-repeat;
                }
                .plt-table thead th:last-child::after {
                  top: 0; bottom: 0; right: 0; width: 8px; height: auto;
                  background:
                    radial-gradient(circle at 0 8px, transparent 0 8px, #FFFFFF 8px) right top / 8px 8px no-repeat,
                    radial-gradient(circle at 0 0,   transparent 0 8px, #FFFFFF 8px) right bottom / 8px 8px no-repeat;
                }
                /* Card corners: 1px #DEE2E6 border, 8px radius. The box hangs 1px past the
                   padding box so it covers the border too; the circle centre is the corner
                   of the 8px radius, the 7–8px ring redraws the border across the notch. */
                .plt-table .plt-stock-row td:first-child::before {
                  top: -1px; left: -1px;
                  background: radial-gradient(circle at 8px 8px, transparent 0 7px, #DEE2E6 7px 8px, #FFFFFF 8px);
                }
                .plt-table .plt-stock-row td:last-child::after {
                  top: -1px; right: -1px;
                  background: radial-gradient(circle at 1px 8px, transparent 0 7px, #DEE2E6 7px 8px, #FFFFFF 8px);
                }
                .plt-table .plt-group-bottom td:first-child::before {
                  bottom: -1px; left: -1px;
                  background: radial-gradient(circle at 8px 1px, transparent 0 7px, #DEE2E6 7px 8px, #FFFFFF 8px);
                }
                .plt-table .plt-group-bottom td:last-child::after {
                  bottom: -1px; right: -1px;
                  background: radial-gradient(circle at 1px 1px, transparent 0 7px, #DEE2E6 7px 8px, #FFFFFF 8px);
                }
                .plt-expand-btn {
                    background: none; border: none; cursor: pointer;
                    display: flex; align-items: center; gap: 4px;
                    font-family: Montserrat; font-size: 12px; font-style: normal;
                    font-weight: 600; line-height: normal;
                    color: #6C757D; padding: 2px 0px;
                }
                .plt-expand-btn:focus { outline: none; }

                /* ── Card grouping (ERP enhancement #1) ──────────────────────
                   A stock and its listings read as one card. The card is built
                   from the CELLS, not from a wrapping element: a wrapper would
                   have to be a table-row ancestor, and that breaks
                   position:sticky on the first/last column. So the parent row
                   paints white and rounds the top two corners, the count strip
                   and the sub-rows paint grey, and whichever row ends the group
                   rounds the bottom two. A transparent spacer row makes the gap
                   between cards. Column resize / horizontal scroll / sticky
                   edges are untouched. */
                .plt-table tbody tr { background: transparent !important; }

                /* Parent stock row — white, top of the card */
                .plt-stock-row td {
                    background: #FFFFFF !important;
                    border-top: 1px solid #DEE2E6 !important;
                    border-bottom: 1px solid #DEE2E6 !important;
                }
                .plt-stock-row td:first-child { border-top-left-radius: 8px; }
                .plt-stock-row td:last-child  { border-top-right-radius: 8px; }

                /* The card's side edges. Every row in a group draws the left
                   border on its first cell and the right border on its last,
                   so the outline closes around the whole card — without them
                   the group reads as loose horizontal rules, not a card.
                   Middle cells draw neither. */
                .plt-stock-row td:first-child,
                .plt-count-row td:first-child,
                .plt-subrow td:first-child { border-left: 1px solid #DEE2E6 !important; }
                .plt-stock-row td:last-child,
                .plt-count-row td:last-child,
                .plt-subrow td:last-child { border-right: 1px solid #DEE2E6 !important; }

                /* Whichever row ends the card rounds the bottom corners —
                   the count strip when collapsed, the last sub-row when not. */
                .plt-group-bottom td:first-child { border-bottom-left-radius: 8px; }
                .plt-group-bottom td:last-child  { border-bottom-right-radius: 8px; }

                /* Header bar — rounded like the reference, and one uniform
                   colour so the rounded ends show no seam. */
                .plt-table thead th { background: #E9ECEF !important; }
                .plt-table thead th:first-child { border-radius: 8px 0 0 8px; }
                .plt-table thead th:last-child  { border-radius: 0 8px 8px 0; }

                /* 10px gap between cards */
                .plt-group-gap td {
                    background: transparent !important;
                    border: none !important;
                    padding: 0 !important;
                    height: 10px;
                }

                /* A stock group stacks one line per platform mapping in the
                   Platforms / Platform Stock / Platform Price columns, so a
                   heavily-mapped stock renders a row taller than the viewport.
                   Cells default to middle alignment, which parks the Stock
                   name, the Notes and the Action button at that row's vertical
                   centre — scrolled out of sight and impossible to click.
                   Top-align them so each stays beside the row's first line. */
                .plt-stock-row td { vertical-align: top; }

                /* Count strip — grey, sits between the parent and its children */
                .plt-count-row td {
                    background: #F8F9FA !important;
                    border-top: none !important;
                    border-bottom: 1px solid #DEE2E6 !important;
                }

                /* Sub-rows (expanded detail rows) — grey, part of the card */
                .plt-subrow td {
                    font-size: 13px;
                    background: #F8F9FA !important;
                    border-bottom: 1px solid #DEE2E6 !important;
                    vertical-align: top;
                }

                /* Child indent. The elbow sits 32px in — where the parent's
                   40px thumbnail is — and the child's TEXT lines up with the
                   parent's text rather than being pushed further right, which
                   is what the reference does:
                     padding 32 + elbow 8 + gap 20 == padding 10 + thumb 40 + gap 10 */
                .plt-subrow td:first-child { padding-left: 32px !important; }
                .plt-subrow-elbow {
                    color: #6C757D;
                    font-size: 12px;
                    line-height: 16px;
                    flex-shrink: 0;
                }

                /* Col 1 — Product Name: max 280px, 2-line clamp */
                .plt-subrow td:nth-child(1) {
                    max-width: 280px;
                }
                .plt-col1-name {
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                    word-break: break-word;
                    white-space: normal;
                    font-size: 12px;
                    font-weight: 400;
                    line-height: 1.4;
                }

                /* Col 2 — SKU Name: max 200px, 2-line clamp */
                .plt-subrow td:nth-child(2) {
                    max-width: 200px;
                }
                .plt-col2-name {
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                    word-break: break-word;
                    white-space: normal;
                    font-size: 12px;
                    line-height: 1.4;
                }

                .plt-listing-count {
                    font-family: Montserrat; font-size: 12px; font-style: normal;
                    font-weight: 600; line-height: normal; color: #6C757D;
                }

                /* Branch balance popup */
                .plt-branch-popup {
                    position: fixed;
                    background: #495057;
                    border-radius: 8px;
                    padding: 16px;
                    z-index: 9999;
                    box-shadow: 0px 4px 20px rgba(0,0,0,0.32);
                    font-family: Montserrat;
                    pointer-events: all;
                    white-space: nowrap;
                }
                /* downward-pointing arrow at bottom center */
                .plt-branch-popup::after {
                    content: '';
                    position: absolute;
                    bottom: -8px;
                    left: 50%;
                    transform: translateX(-50%);
                    border-left: 8px solid transparent;
                    border-right: 8px solid transparent;
                    border-top: 8px solid #343A40;
                }
                .plt-branch-table {
                    border-collapse: collapse;
                }
                .plt-branch-table td {
                    font-size: 12px;
                    color: #FFF;
                    padding: 4px 0;
                    vertical-align: middle;
                }
                .plt-branch-table td.plt-b-name {
                    font-weight: 400;
                    padding-right: 10px;
                }
                .plt-branch-table td.plt-b-count {
                    font-weight: 600;
                    text-align: right;
                    padding-right: 28px;
                }
                .plt-branch-table td.plt-b-count:last-child {
                    padding-right: 0;
                }

                /* Stock Qty inconsistency (ERP In Stock ≠ Platform Stock) */
                .plt-incon-wrap {
                    display: inline-flex;
                    align-items: center;
                    flex-direction: row;
                    gap: 5px;
                    width: fit-content;
                }
                .plt-incon-badge {
                    display: inline-flex;
                    justify-content: center;
                    align-items: center;
                    padding: 2px 5px;
                    background: #CC9A06;
                    border: 0.5px solid #CC9A06;
                    border-radius: 4px;
                    color: #FFFFFF;
                    font-family: Montserrat;
                    font-size: 12px;
                    font-weight: 600;
                    line-height: normal;
                    white-space: nowrap;
                }
                .plt-incon-info {
                    display: inline-flex;
                    align-items: center;
                    cursor: pointer;
                    flex-shrink: 0;
                }
                .plt-incon-tip {
                    position: fixed;
                    max-width: 220px;
                    white-space: normal;
                    background: #495057;
                    color: #FFFFFF;
                    border-radius: 8px;
                    padding: 10px 12px;
                    font-family: Montserrat;
                    font-size: 12px;
                    font-weight: 400;
                    line-height: 1.4;
                    box-shadow: 0px 4px 20px rgba(0,0,0,0.32);
                    z-index: 9999;
                    pointer-events: none;
                }
                .plt-incon-tip::after {
                    content: '';
                    position: absolute;
                    bottom: -8px;
                    left: 50%;
                    transform: translateX(-50%);
                    border-left: 8px solid transparent;
                    border-right: 8px solid transparent;
                    border-top: 8px solid #495057;
                }

                /* Normal Price (Lazada list price) info icon + tooltip */
                .plt-normprice-info {
                    display: inline-flex;
                    align-items: center;
                    cursor: pointer;
                    flex-shrink: 0;
                }
                .plt-normprice-tip {
                    position: fixed;
                    width: 309px;
                    box-sizing: border-box;
                    white-space: normal;
                    background: #F8F9FA;
                    color: #212529;
                    border-radius: 8px;
                    border: 1px solid #E9ECEF;
                    padding: 10px;
                    font-family: Montserrat;
                    font-size: 12px;
                    font-weight: 400;
                    line-height: 1.4;
                    z-index: 9999;
                    pointer-events: none;
                }
                .plt-normprice-tip-title {
                    font-weight: 700;
                    margin-bottom: 4px;
                }
            `}</style>

            <div className="breadcrumb">
                {breadcrumbs.map((crumb, i) => (
                    <span
                        key={i}
                        className={`breadcrumb-item${crumb === 'Platform Listing' ? ' clickable' : ''}`}
                        onClick={() => { if (crumb === 'Platform Listing') history.push('/listing-pricing'); }}
                        style={crumb === 'Platform Listing' ? { cursor: 'pointer' } : {}}
                    >
                        {crumb}{i !== breadcrumbs.length - 1}
                    </span>
                ))}
            </div>

            {/* One slot at the top of the page: an action's progress, then its
                result. All three are pinned to the same fixed 86px offset, so
                they render mutually exclusively rather than stacking. A row
                action fired at the bottom of a long table is reported up here,
                where the user is looking, instead of on a button they have
                scrolled past. */}
            {stockActionLoading ? (
                <LoadingNotification message={stockActionLabel || 'Updating 1 Listing'} total={1} />
            ) : actionResult ? (
                <AlertNotification
                    type={actionResult.type}
                    message={actionResult.message}
                    recordName="Listing"
                    action="update"
                    onClose={() => setActionResult(null)}
                />
            ) : successMessage ? (
                <AlertNotification
                    type="success"
                    message={successMessage}
                    recordName="Listing"
                    action="create"
                    onClose={() => setSuccessMessage('')}
                />
            ) : null}

            <div className="employee-tabs-container">
                <div className="tab-content">
                    <div className="info-section">
                        <div className="info-card" style={{ overflow: 'unset' }}>
                            <div className="entities-tab">

                                {/* Toolbar */}
                                <div className="toolbar">
                                    <div className="toolbar-left" style={{ flex: 1, minWidth: 0, marginRight: '0.75rem' }}>{/* flex:1 so the two search boxes are sized by the row rather than fixed.
                                        marginRight because .toolbar is justify-content:space-between
                                        with NO gap of its own — the gap only exists inside -left/-right.
                                        While -left was content-width, space-between supplied the space;
                                        once it fills the row the filter button lands flush against
                                        Add Listing. 0.75rem matches the gap used within each group. */}
                                        {/* ERP-5233: the single search box is replaced by the two
                                            below. Kept commented rather than deleted so it can come
                                            back quickly if the two-box search has to be rolled back.

                                        <SearchBox
                                            searchInput={searchInput}
                                            setSearchInput={setSearchInput}
                                            setSearchTerm={setSearchTerm}
                                            setCurrentPage={setCurrentPage}
                                            disabled={isSearchFilterDisabled}
                                        />
                                        */}
                                        <ListingSearchBars
                                            modelValue={modelSearchInput}
                                            onModelValueChange={setModelSearchInput}
                                            onModelCommit={(v, exact) => { setModelSearch({ term: v, exact: !!exact }); setCurrentPage(1); }}
                                            detailValue={detailSearchInput}
                                            onDetailValueChange={setDetailSearchInput}
                                            onDetailCommit={(v) => { setDetailSearch(v); setCurrentPage(1); }}
                                            disabled={isSearchFilterDisabled}
                                        />
                                        <div style={{ position: 'relative' }}>
                                            <button
                                                ref={filterButtonRef}
                                                className={`icon-btn p-2 filter-btn ${filtersApplied ? 'applied' : ''}`}
                                                disabled={isSearchFilterDisabled}
                                                onClick={() => {
                                                    if (isSearchFilterDisabled) return;
                                                    if (!filterPopupOpen) {
                                                        // Pre-populate temp values with currently applied filters
                                                        setTempValues({
                                                            status: filters.status || [],
                                                            platforms: filters.platforms || [],
                                                            stockSync: filters.stockSync || [],
                                                            consistency: filters.consistency || [],
                                                            setTypes: filters.setTypes || [],
                                                        });
                                                    }
                                                    setFilterPopupOpen(o => !o);
                                                }}
                                                style={{
                                                    border: isSearchFilterDisabled
                                                        ? '1px solid var(--Gray-400, #CED4DA)'
                                                        : filtersApplied
                                                            ? '1px solid #2C7BE5'
                                                            : '1px solid #6C757D',
                                                    color: isSearchFilterDisabled ? '#CED4DA' : filtersApplied ? '#2C7BE5' : '#6C757D',
                                                    background: isSearchFilterDisabled
                                                        ? 'var(--Gray-100, #F8F9FA)'
                                                        : filtersApplied
                                                            ? '#CFE2FF'
                                                            : '#FFFFFF',
                                                    height: 40,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    borderRadius: 8,
                                                    cursor: isSearchFilterDisabled ? 'not-allowed' : 'pointer',
                                                    pointerEvents: isSearchFilterDisabled ? 'none' : 'auto',
                                                }}
                                            >
                                                {isSearchFilterDisabled ? <FilterDisabledIcon /> : filtersApplied ? <FilterActiveIcon /> : <FilterInactiveIcon />}
                                            </button>
                                            <FilterDrawer
                                                isOpen={filterPopupOpen}
                                                onClose={() => {
                                                    setTempValues({ status: [], platforms: [], stockSync: [], consistency: [], setTypes: [] });
                                                    setFilterPopupOpen(false);
                                                }}
                                                sections={filterSections}
                                                tempValues={tempValues}
                                                setTempValues={setTempValues}
                                                onApply={() => {
                                                    const next = {
                                                        status: tempValues.status || [],
                                                        platforms: tempValues.platforms || [],
                                                        stockSync: tempValues.stockSync || [],
                                                        consistency: tempValues.consistency || [],
                                                        setTypes: tempValues.setTypes || [],
                                                    };
                                                    setFilters(next);
                                                    setAppliedFilter(next);
                                                    setFilterPopupOpen(false);
                                                }}
                                                onClear={() => {
                                                    const reset = { status: [], platforms: [], stockSync: [], consistency: [], setTypes: [] };
                                                    setTempValues(reset);
                                                    setFilters(reset);
                                                    setAppliedFilter(null);
                                                }}
                                                enableAutoSwap={true}
                                            />
                                        </div>
                                    </div>
                                    <div className="toolbar-right" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                        {/* ERP-5254: Import — the Excel listing-mapping flow; the template download lives on its step 1 (same button as Platform Pricing) */}
                                        <button
                                            type="button"
                                            onClick={() => history.push('/listing-pricing/import')}
                                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, height: 40, minWidth: 100, padding: '10px 14px', borderRadius: 8, border: '1px solid #212529', background: '#fff', color: '#212529', boxShadow: '0 1px 4px 0 rgba(0,0,0,0.10)', cursor: 'pointer', marginRight: 6 }}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 14.9A7 7 0 1 1 15.7 8h1.8a4.5 4.5 0 0 1 2.5 8.2" /><path d="M12 12v9" /><path d="m16 16-4-4-4 4" /></svg>
                                            <span style={{ fontSize: 12, fontWeight: 600 }}>Import</span>
                                        </button>
                                        <button className="icon-btn dark-border" onClick={() => history.push('/listing-pricing/add-listing')}>
                                            <Plus size={18} /> Add Listing
                                        </button>
                                    </div>
                                </div>

                                {/* Table */}
                                <div
                                    className="table-wrapper"
                                    style={{
                                        border: 'none',
                                        opacity: tableLoading ? 0.5 : 1,
                                        pointerEvents: tableLoading ? 'none' : undefined,
                                        transition: 'opacity .15s ease',
                                    }}
                                    ref={pltTableScrollRef}
                                    onScroll={handlePltTableScroll}
                                >
                                    {isTableEmpty && !anySearchActive && !filtersApplied ? (
                                        <div className="empty-state">
                                            <div className="empty-state-content">
                                                <div className="icon">
                                                    <img src={noBrandImage} alt="empty" style={{ width: 120, height: 120 }} />
                                                </div>
                                                <h1 className="no-item-title mt-2">No Listings Found</h1>
                                                <p className="no-item-text">Nothing here yet. Add your first item to get started!</p>
                                            </div>
                                        </div>
                                    ) : isTableEmpty ? (
                                        <div className="empty-state">
                                            <div className="empty-state-content">
                                                <div className="icon">
                                                    <img src={emptySearchImg} alt="no results" style={{ width: 120, height: 120 }} />
                                                </div>
                                                <h1 className="no-item-title mt-2">No Listings Found</h1>
                                                <p className="no-item-text">No results match what you're looking for</p>
                                            </div>
                                        </div>
                                    ) : (
                                        <table
                                            className={`entities-table plt-table${pltScrolledLeft ? ' is-scrolled-left' : ''}${pltScrolledRight ? ' is-scrolled-right' : ''}`}
                                            style={{ borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed', width: '100%' }}
                                        >
                                            <colgroup>
                                                <col style={{ width: colWidths.stock }} />
                                                <col style={{ width: colWidths.inStock }} />
                                                <col style={{ width: colWidths.costPrice }} />
                                                <col style={{ width: colWidths.platforms }} />
                                                <col style={{ width: colWidths.platformStock }} />
                                                <col style={{ width: colWidths.basePrice }} />
                                                <col style={{ width: colWidths.platformPrice }} />
                                                <col style={{ width: colWidths.notes }} />
                                                <col style={{ width: 84 }} />
                                            </colgroup>
                                            <thead>
                                                <tr>
                                                    <th className="plt-sticky-edge" style={{ position: 'sticky', left: 0, zIndex: 5, overflow: 'visible' }}>
                                                        Stock
                                                        <ColResizeHandle colKey="stock" minWidth={150} />
                                                    </th>
                                                    <th style={{ position: 'relative', overflow: 'hidden' }}>
                                                        In Stock
                                                        <ColResizeHandle colKey="inStock" />
                                                    </th>
                                                    <th style={{ position: 'relative', overflow: 'hidden' }}>
                                                        Cost Price
                                                        <ColResizeHandle colKey="costPrice" />
                                                    </th>
                                                    <th style={{ position: 'relative', overflow: 'hidden' }}>
                                                        Platforms
                                                        <ColResizeHandle colKey="platforms" />
                                                    </th>
                                                    <th style={{ position: 'relative', overflow: 'hidden' }}>
                                                        Platform Stock
                                                        <ColResizeHandle colKey="platformStock" />
                                                    </th>
                                                    <th style={{ position: 'relative', overflow: 'hidden' }}>
                                                        Base Price
                                                        <ColResizeHandle colKey="basePrice" />
                                                    </th>
                                                    <th style={{ position: 'relative', overflow: 'hidden' }}>
                                                        Platform Price
                                                        <ColResizeHandle colKey="platformPrice" />
                                                    </th>
                                                    <th style={{ position: 'relative', overflow: 'hidden' }}>
                                                        Notes
                                                        <ColResizeHandle colKey="notes" />
                                                    </th>
                                                    <th className="plt-action-edge" style={{ textAlign: 'center', position: 'sticky', right: 0, zIndex: 5, overflow: 'visible' }}>
                                                        Action
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {/* 10px between the header and the first card — the design
                                                    spaces them exactly like two cards, not flush. */}
                                                <tr className="plt-group-gap" aria-hidden="true">
                                                    <td colSpan={9} />
                                                </tr>
                                                {grouped.map((group, gIdx) => {
                                                    const isExpanded = !!expandedStocks[group.key];
                                                    // carry the owning listing so a sub-row opens its own listing, not the group's first
                                                    const allPlatforms = group.listings.flatMap(l => l.platforms.map(p => ({ ...p, _listing: l })));
                                                    // ERP-5171 — see PLACEHOLDER_PARENT_ROW_LIMIT. Only the parent row is
                                                    // capped: the count row and the sub-rows still cover every mapping.
                                                    const rowPlatforms = isPlaceholderStock(group.name, group.variant)
                                                        ? allPlatforms.slice(0, PLACEHOLDER_PARENT_ROW_LIMIT)
                                                        : allPlatforms;
                                                    const hasHiddenPlatforms = rowPlatforms.length < allPlatforms.length;

                                                    return (
                                                        <React.Fragment key={group.key}>
                                                            {/* Parent stock row — dashed bottom connects to count row */}
                                                            <tr
                                                                className="plt-stock-row"
                                                                style={{ cursor: 'pointer' }}
                                                                onClick={() => setDrawer({ type: 'listing', listing: group.listings[0], group })}
                                                            >
                                                                {/* Stock */}
                                                                <td className="plt-sticky-edge" style={{ position: 'sticky', left: 0, zIndex: 4, background: '#fff', overflow: 'hidden' }}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                                                                        <img
                                                                            src={group.avatar}
                                                                            alt="avatar"
                                                                            style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
                                                                        />
                                                                        <div style={{ minWidth: 0, overflow: 'hidden' }}>
                                                                            <div style={{ fontWeight: 400, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{group.name}</div>
                                                                            <div className="sub-line mt-1" style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{group.variant}{group.model_number ? ` | ${group.model_number}` : ''}</div>
                                                                            <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                                                                                <StockTags condition={group.listings[0]?.condition} conditionType={group.listings[0]?.grading} setType={group.setType} style={{ minWidth: 'unset', padding: '2px 8px', fontSize: '8px', lineHeight: 'normal', background: '#FFEB00' }} />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </td>

                                                                {/* In Stock */}
                                                                <td>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                                                        <span>{group.inStock}</span>
                                                                        {group.inStock > 0 &&(<span
                                                                            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', flexShrink: 0 }}
                                                                            onMouseEnter={e => openBranchPopup(e, group.stockId)}
                                                                            onMouseLeave={closeBranchPopup}
                                                                            onClick={e => { e.stopPropagation(); openBranchPopup(e, group.stockId); }}
                                                                        >
                                                                            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                                                                                <g clipPath="url(#clip0_plt_info)">
                                                                                    <path fillRule="evenodd" clipRule="evenodd" d="M5 9.375C6.16032 9.375 7.27312 8.91406 8.09359 8.09359C8.91406 7.27312 9.375 6.16032 9.375 5C9.375 3.83968 8.91406 2.72688 8.09359 1.90641C7.27312 1.08594 6.16032 0.625 5 0.625C3.83968 0.625 2.72688 1.08594 1.90641 1.90641C1.08594 2.72688 0.625 3.83968 0.625 5C0.625 6.16032 1.08594 7.27312 1.90641 8.09359C2.72688 8.91406 3.83968 9.375 5 9.375ZM5 10C6.32608 10 7.59785 9.47322 8.53553 8.53553C9.47322 7.59785 10 6.32608 10 5C10 3.67392 9.47322 2.40215 8.53553 1.46447C7.59785 0.526784 6.32608 0 5 0C3.67392 0 2.40215 0.526784 1.46447 1.46447C0.526784 2.40215 0 3.67392 0 5C0 6.32608 0.526784 7.59785 1.46447 8.53553C2.40215 9.47322 3.67392 10 5 10Z" fill="#5E6E82"/>
                                                                                    <path d="M5.58122 4.11768L4.14997 4.29705L4.09872 4.53455L4.37997 4.58643C4.56372 4.63018 4.59997 4.69643 4.55997 4.87955L4.09872 7.04705C3.97747 7.60768 4.16435 7.87143 4.60372 7.87143C4.94435 7.87143 5.33997 7.71393 5.51935 7.49768L5.57434 7.23768C5.44934 7.34768 5.26684 7.39143 5.1456 7.39143C4.97372 7.39143 4.91122 7.2708 4.9556 7.0583L5.58122 4.11768Z" fill="#5E6E82"/>
                                                                                    <path d="M5 3.4375C5.34518 3.4375 5.625 3.15768 5.625 2.8125C5.625 2.46732 5.34518 2.1875 5 2.1875C4.65482 2.1875 4.375 2.46732 4.375 2.8125C4.375 3.15768 4.65482 3.4375 5 3.4375Z" fill="#5E6E82"/>
                                                                                </g>
                                                                                <defs>
                                                                                    <clipPath id="clip0_plt_info">
                                                                                        <rect width="10" height="10" fill="white"/>
                                                                                    </clipPath>
                                                                                </defs>
                                                                            </svg>
                                                                        </span>)}
                                                                    </div>
                                                                    {/* Units already owed to open platform orders. Hidden at zero:
                                                                        on a full page the line is noise on every row that has no
                                                                        orders outstanding, and In Stock already tells that story. */}
                                                                    {group.committedQty > 0 && (
                                                                        <div style={COMMITTED_STYLE}>
                                                                            Committed: {group.committedQty}
                                                                        </div>
                                                                    )}
                                                                </td>

                                                                {/* Cost Price — uniform: show single price; range: show min → max */}
                                                                <td style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                                                    {group.costPriceData
                                                                        ? group.costPriceData.is_uniform
                                                                            ? `SGD ${formatPrice(group.costPriceData.min)}`
                                                                            : <span>SGD {formatPrice(group.costPriceData.min)}<span style={{ margin: '0 4px', color: '#6C757D' }}>→</span>SGD {formatPrice(group.costPriceData.max)}</span>
                                                                        : <span style={{ color: '#ADB5BD' }}>-</span>}
                                                                </td>

                                                                {/* Platforms */}
                                                                <td style={{ overflow: 'hidden' }}>
                                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                                                                        {allPlatforms.length > 0 ? rowPlatforms.map((p, i) => (
                                                                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, minWidth: 0 }}>
                                                                                <svg width="8" height="8" viewBox="0 0 8 8" fill="none" style={{ flexShrink: 0 }}>
                                                                                    <circle cx="4" cy="4" r="3" fill={p.dotColor} />
                                                                                </svg>
                                                                                <span style={{ color: p.textColor, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{p.name}</span>
                                                                                {p.isCampaign && (
                                                                                    <span title={CAMPAIGN_TAG_TITLE} style={CAMPAIGN_TAG_STYLE}>
                                                                                        Campaign
                                                                                    </span>
                                                                                )}
                                                                                {p.isDelisted && (
                                                                                    <span style={DELIST_TAG_STYLE}>
                                                                                        Delist
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        )) : <span style={{ color: '#ADB5BD' }}>-</span>}
                                                                        {hasHiddenPlatforms && <MoreMappingsEllipsis />}
                                                                    </div>
                                                                </td>

                                                                {/* Platform Stock */}
                                                                <td style={{ overflow: 'hidden' }}>
                                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                                        {allPlatforms.length > 0 ? rowPlatforms.map((p, i) => (
                                                                            <div key={i} style={{ fontSize: 12, color: p.isFailed ? p.textColor : '#212529', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                                                                {p.isPending ? '-' : isStockInconsistent(p, erpAvailableQty(group, null))
                                                                                    ? <StockInconsistencyBadge>{p.platformStock}</StockInconsistencyBadge>
                                                                                    : p.platformStock === 0
                                                                                    ? <span style={{ color: '#58151C', fontFamily: 'Montserrat', fontSize: 12, fontWeight: 400, lineHeight: 'normal' }}>Sold Out</span>
                                                                                    : (p.platformStock ?? '-')}
                                                                            </div>
                                                                        )) : <span style={{ color: '#ADB5BD' }}>-</span>}
                                                                        {hasHiddenPlatforms && <MoreMappingsEllipsis />}
                                                                    </div>
                                                                </td>

                                                                {/* Base Price — show once for the group */}
                                                                <td style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                                                    {(() => {
                                                                        const first = allPlatforms.find(p => p.basePrice != null && p.basePrice !== 0);
                                                                        return first
                                                                            ? <span style={{ fontSize: 12, color: '#212529' }}>{`${first.basePriceCurrency} ${formatPrice(first.basePrice)}`}</span>
                                                                            : <span style={{ color: '#ADB5BD' }}>-</span>;
                                                                    })()}
                                                                </td>

                                                                {/* Platform Price */}
                                                                <td style={{ overflow: 'hidden' }}>
                                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                                        {allPlatforms.length > 0 ? rowPlatforms.map((p, i) => (
                                                                            <div key={i} style={{ fontSize: 12, color: p.isFailed ? p.textColor : '#212529', whiteSpace: 'nowrap' }}>
                                                                                {p.isFailed || p.isPending || !p.platformPrice
                                                                                    ? '-'
                                                                                    : renderPlatformPriceValue(p)}
                                                                            </div>
                                                                        )) : <span style={{ color: '#ADB5BD' }}>-</span>}
                                                                        {hasHiddenPlatforms && <MoreMappingsEllipsis />}
                                                                    </div>
                                                                </td>

                                                                {/* Notes — stock-level remark, read-only. Edited on Add/Edit Listing. */}
                                                                <td style={{ overflow: 'hidden' }}>
                                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                                        {(() => {
                                                                            const noted = group.listings.filter(l => (l.notes || '').trim());
                                                                            if (!noted.length) return <span style={{ color: '#ADB5BD' }}>-</span>;
                                                                            return noted.map(l => (
                                                                                <div key={l.id} title={l.notes} style={{ fontSize: 12, color: '#212529', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                                                                    {l.notes}
                                                                                </div>
                                                                            ));
                                                                        })()}
                                                                    </div>
                                                                </td>

                                                                {/* Action column on stock row — always show ... for single-listing groups */}
                                                                <td className="plt-action-edge" style={{ textAlign: 'center', verticalAlign: 'top', position: 'sticky', right: 0, zIndex: 4, background: '#fff' }}>
                                                                    {group.listings.length === 1 && (
                                                                        <button
                                                                            ref={el => (buttonRefs.current[group.listings[0].id] = el)}
                                                                            className="icon-btn plt-action-btn"
                                                                            onClick={e => { e.stopPropagation(); toggleDropdown(group.listings[0].id); }}
                                                                        >
                                                                            <MoreHorizontal size={18} />
                                                                        </button>
                                                                    )}
                                                                </td>
                                                            </tr>

                                                            {/* Count row — entire row clickable to expand/collapse */}
                                                            <tr
                                                                className={`plt-count-row${isExpanded ? '' : ' plt-group-bottom'}`}
                                                                style={{ cursor: 'pointer' }}
                                                                onClick={() => toggleExpand(group.key)}
                                                            >
                                                                <td className="plt-sticky-edge" style={{ padding: '14px 10px', position: 'sticky', left: 0, zIndex: 4, background: '#F8F9FA' }}>
                                                                    <span className="plt-listing-count">
                                                                        {allPlatforms.length} {allPlatforms.length > 1 ? 'Listings' : 'Listing'}
                                                                    </span>
                                                                </td>
                                                                <td colSpan={7} />
                                                                <td className="plt-action-edge" style={{ textAlign: 'right', padding: '14px 6px 14px 4px', position: 'sticky', right: 0, zIndex: 4, background: '#F8F9FA' }}>
                                                                    <span className="plt-expand-btn" style={{ justifyContent: 'flex-end', display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                                                                        {isExpanded ? (
                                                                            <><span>Collapse</span><ChevronUp size={14} /></>
                                                                        ) : (
                                                                            <><span>Expand</span><ChevronDown size={14} /></>
                                                                        )}
                                                                    </span>
                                                                </td>
                                                            </tr>

                                                            {/* Expanded sub-rows: one row per platform mapping */}
                                                            {isExpanded && allPlatforms.map((p, pi) => {
                                                                const isLastSubrow = pi === allPlatforms.length - 1;

                                                                // helpers — treat null / empty / 'Invalid ID' as missing
                                                                const cleanStr = (v) =>
                                                                    (v && v !== 'Invalid ID') ? v : null;

                                                                const productName = cleanStr(p.productName);
                                                                const productId   = cleanStr(p.platformProductId);
                                                                const skuName     = cleanStr(p.skuName);
                                                                const skuId       = cleanStr(p.platformSkuId);

                                                                const hasStatus  = !!p.lastSyncStatus;
                                                                const isFailed   = p.isFailed;
                                                                const isPending  = p.isPending;

                                                                return (
                                                                    <tr
                                                                        key={`${p.name}-${pi}`}
                                                                        className={isLastSubrow ? 'plt-subrow plt-subrow-last plt-group-bottom' : 'plt-subrow'}
                                                                        style={{ cursor: 'pointer' }}
                                                                        onClick={() => setDrawer({ type: 'platform', listing: p._listing || group.listings[0], platform: p, group })}
                                                                    >
                                                                        {/* Col 1 — elbow + Product Name (2-line clamp, max 280px) + Product ID.
                                                                            The 32px indent lives in .plt-subrow td:first-child so the
                                                                            elbow lines up with the padding edge, not with the text. */}
                                                                        <td className="plt-sticky-edge" style={{ fontWeight: 400, position: 'sticky', left: 0, zIndex: 3, background: '#F8F9FA' }}>
                                                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, minWidth: 0 }}>
                                                                                <span className="plt-subrow-elbow" aria-hidden="true">└</span>
                                                                                <div style={{ minWidth: 0, flex: 1 }}>
                                                                                    {productName
                                                                                        ? <div className="plt-col1-name" style={{ color: p.textColor }}>{productName}</div>
                                                                                        : <span style={{ color: '#6C757D' }}>-</span>}
                                                                                    {productId && (
                                                                                        <div style={{ fontSize: 10, color: '#6C757D', marginTop: 2, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                                                                            Product ID: {productId}
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </td>

                                                                        {/* Col 2 — SKU Name (2-line clamp, max 200px) + SKU ID */}
                                                                        <td style={{ overflow: 'hidden' }}>
                                                                            {skuName
                                                                                ? <div className="plt-col2-name" style={{ color: p.textColor }}>{skuName}</div>
                                                                                : <span style={{ color: '#6C757D' }}>-</span>}
                                                                            {skuId && (
                                                                                <div style={{ fontSize: 10, color: '#6C757D', marginTop: 2, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                                                                    SKU ID: {skuId}
                                                                                </div>
                                                                            )}
                                                                        </td>

                                                                        {/* Col 3 — Cost Price (blank for sub-rows) */}
                                                                        <td></td>

                                                                        {/* Col 4 — Platform dot + name */}
                                                                        <td style={{ overflow: 'hidden' }}>
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, minWidth: 0 }}>
                                                                                <svg width="8" height="8" viewBox="0 0 8 8" fill="none" style={{ flexShrink: 0 }}>
                                                                                    <circle cx="4" cy="4" r="3" fill={p.dotColor} />
                                                                                </svg>
                                                                                <span style={{ color: p.textColor, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{p.name}</span>
                                                                                {p.isCampaign && (
                                                                                    <span title={CAMPAIGN_TAG_TITLE} style={CAMPAIGN_TAG_STYLE}>
                                                                                        Campaign
                                                                                    </span>
                                                                                )}
                                                                                {p.isDelisted && (
                                                                                    <span style={DELIST_TAG_STYLE}>
                                                                                        Delist
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </td>

                                                                        {/* Col 5 — Platform Stock */}
                                                                        <td style={{ fontSize: 12, color: p.textColor, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                                                            {isFailed || isPending || !hasStatus ? '-' : isStockInconsistent(p, erpAvailableQty(group, null))
                                                                                ? <StockInconsistencyBadge>{p.platformStock}</StockInconsistencyBadge>
                                                                                : p.platformStock === 0
                                                                                ? <span style={{ color: '#58151C', fontFamily: 'Montserrat', fontSize: 12, fontWeight: 400, lineHeight: 'normal' }}>Sold Out</span>
                                                                                : (p.platformStock ?? '-')}
                                                                        </td>

                                                                        {/* Col 6 — Base Price from sales_listing_platform_price */}
                                                                        <td style={{ fontSize: 12, color: '#212529', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                                                            {p.basePrice != null && p.basePrice !== 0
                                                                                ? `${p.basePriceCurrency} ${formatPrice(p.basePrice)}`
                                                                                : <span style={{ color: '#ADB5BD' }}>-</span>}
                                                                        </td>

                                                                        {/* Col 7 — Platform Price */}
                                                                        <td style={{ fontSize: 12, color: p.textColor, whiteSpace: 'nowrap' }}>
                                                                            {isFailed || isPending || !hasStatus || !p.platformPrice
                                                                                ? '-'
                                                                                : renderPlatformPriceValue(p)}
                                                                        </td>

                                                                        {/* Col 8 — Notes (listing-level remark, read-only) */}
                                                                        <td title={p.notes || ''} style={{ fontSize: 12, color: '#212529', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                                                            {(p.notes || '').trim() || <span style={{ color: '#ADB5BD' }}>-</span>}
                                                                        </td>

                                                                        {/* Col 9 — Action */}
                                                                        <td className="plt-action-edge" style={{ textAlign: 'center', position: 'sticky', right: 0, zIndex: 3, background: '#F8F9FA' }}>
                                                                            <button
                                                                                ref={el => (buttonRefs.current[`plt-${group.key}-${pi}`] = el)}
                                                                                className="icon-btn plt-action-btn"
                                                                                onClick={e => { e.stopPropagation(); toggleDropdown(`plt-${group.key}-${pi}`); }}
                                                                            >
                                                                                <MoreHorizontal size={18} />
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}

                                                            {/* Gap between cards. A spacer row rather than a margin:
                                                                table rows cannot take one, and border-spacing would
                                                                open a gap between every cell, not just between groups. */}
                                                            <tr className="plt-group-gap" aria-hidden="true">
                                                                <td colSpan={9} />
                                                            </tr>
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    )}

                                    {/* Dropdown menu */}
                                    {dropdownOpenId !== null && (
                                        <div
                                            ref={dropdownRef}
                                            className="backdrop-cls"
                                            style={{
                                                position: 'fixed',
                                                top: dropdownPosition.top,
                                                left: dropdownPosition.left,
                                                background: 'white',
                                                boxShadow: '0px 1px 4px 0px #0000001A',
                                                zIndex: 1000,
                                                minWidth: 152,
                                                borderRadius: 8,
                                            }}
                                            onClick={e => e.stopPropagation()}
                                        >
                                            <div className="btn-align-forall">
                                                {(() => {
                                                    const key = String(dropdownOpenId);
                                                    const isPlatformRow = key.startsWith('plt-');

                                                    // Resolve listing + platform
                                                    let listing = null;
                                                    let platform = null;
                                                    let group = null;
                                                    if (isPlatformRow) {
                                                        const parts = key.split('-');
                                                        const groupKey = parts[1];
                                                        const pi = Number(parts[2]);
                                                        group = groupByStock(listings).find(g => g.key === groupKey);
                                                        if (group) {
                                                            const allP = group.listings.flatMap(l => l.platforms.map(p => ({ ...p, _listing: l })));
                                                            const hit = allP[pi];
                                                            listing = hit?._listing ?? group.listings[0];
                                                            platform = hit ? { ...hit } : null;
                                                        }
                                                    } else {
                                                        listing = listings.find(l => l.id === Number(key));
                                                        // by listing, not by stock: placeholder-stock listings each have their own group
                                                        group = groupByStock(listings).find(g => g.listings.some(l => l.id === listing?.id));
                                                    }
                                                    if (!listing) return null;

                                                    if (!isPlatformRow) {
                                                        // ── Listing-level dropdown ──
                                                        return (
                                                            <>
                                                                <div className="common-for-all">
                                                                    <button className="plt-dropdown-btn" onClick={() => {
                                                                        setDrawer({ type: 'listing', listing, group });
                                                                        setDropdownOpenId(null);
                                                                    }}>View Details</button>
                                                                </div>
                                                                <div className="common-for-all">
                                                                    <button className="plt-dropdown-btn" onClick={() => {
                                                                        history.push('/listing-pricing/edit-listing', { listing });
                                                                        setDropdownOpenId(null);
                                                                    }}>Edit</button>
                                                                </div>
                                                                <div className="common-for-all">
                                                                    <button className="plt-dropdown-btn deactivate-cls" onClick={() => {
                                                                        setSelectedListingId(listing.id);
                                                                        setArchivedModalOpen(true);
                                                                        setDropdownOpenId(null);
                                                                    }}>Unmap All</button>
                                                                </div>
                                                            </>
                                                        );
                                                                    } else {
                                                                        // ── Platform-level dropdown ──
                                                                        const pName = (platform?.name || '').toLowerCase();
                                                                        const isWoo = pName.includes('woocommerce');
                                                                        const isSupported = pName.includes('lazada') || pName.includes('shopee') || pName.includes('tiktok') || pName.includes('tik tok');
                                                                        const isSoldOut = platform?.stockSyncOption
                                                                            ? platform.stockSyncOption === 'sync_erp'
                                                                            : (platform?.platformStock == null ? true : Number(platform.platformStock) > 0);
                                                                        const isActing = stockActionLoading === platform?.mappingId;
                                                                        // Disable stock action for any non-success sync (failed or pending).
                                                                        const isStockBtnDisabled = isActing || platform?.isFailed || platform?.isPending;
                                                                        const wooIsListed = platform?.platformPrice != null;
                                                                        return (
                                                                            <>
                                                                                <div className="common-for-all">
                                                                                    <button className="plt-dropdown-btn" onClick={() => {
                                                                                        setDrawer({ type: 'platform', listing, platform, group });
                                                                                        setDropdownOpenId(null);
                                                                                    }}>View Details</button>
                                                                                </div>
                                                                                {isWoo && (
                                                                                    <div className="common-for-all">
                                                                                        <button
                                                                                            className="plt-dropdown-btn"
                                                                                            disabled={platform.isDelisted && !wooIsListed}
                                                                                            style={{ opacity: (platform.isDelisted && !wooIsListed) ? 0.4 : 1, cursor: (platform.isDelisted && !wooIsListed) ? 'not-allowed' : 'pointer' }}
                                                                                            onClick={() => { if (!(platform.isDelisted && !wooIsListed)) requestAction(listing, platform, platform.isDelisted ? 'list' : 'delist'); }}
                                                                                        >
                                                                                            {platform.isDelisted ? 'Relist' : 'Delist'}
                                                                                        </button>
                                                                                    </div>
                                                                                )}
                                                                                {isSupported && (
                                                                                    <div className="common-for-all">
                                                                                        <button
                                                                                            className="plt-dropdown-btn"
                                                                                            disabled={isStockBtnDisabled}
                                                                                            style={{ opacity: isStockBtnDisabled && !isActing ? 0.4 : 1, cursor: isStockBtnDisabled ? 'not-allowed' : 'pointer' }}
                                                                                            onClick={() => { if (!isStockBtnDisabled) requestAction(listing, platform, isSoldOut ? 'sold_out' : 'restore'); }}
                                                                                        >
                                                                                            {isActing ? 'Updating...' : isSoldOut ? 'Sold Out' : 'Restore Stock'}
                                                                                        </button>
                                                                                    </div>
                                                                                )}
                                                                                <div className="common-for-all">
                                                                                    <button className="plt-dropdown-btn deactivate-cls" onClick={() => handleUnmapMapping(listing, platform)}>Unmap</button>
                                                                                </div>
                                                                            </>
                                                                        );
                                                                    }
                                                })()}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Pagination */}
                                {listings.length > 0 && (
                                    <div className="pagination" style={{ marginTop: 16 }}>
                                        <div className="pagination-info">
                                            {`${(currentPage - 1) * PAGE_SIZE + 1} - ${Math.min(currentPage * PAGE_SIZE, totalCount)} of ${totalCount}`}
                                        </div>
                                        <Pagination
                                            currentPage={currentPage}
                                            totalPages={totalPages}
                                            onPageChange={setCurrentPage}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modals */}
            {deactivateModalOpen && selectedListing && (
                <ConfirmDeactivateModal
                    isOpen={deactivateModalOpen}
                    onClose={() => setDeactivateModalOpen(false)}
                    onConfirm={() => handleDeactivate(selectedListing.id)}
                    recordName={selectedListing.name}
                />
            )}
            {archivedModalOpen && (
                <ConfirmArchiveModal
                    isOpen={archivedModalOpen}
                    onCancel={() => { setArchivedModalOpen(false); setSelectedListingId(null); }}
                    onConfirm={() => handleArchive(selectedListingId)}
                    title="Confirm to unmap all Listings under this Stock?"
                    description="Once all are unmapped, this Stock and all its mapped Listings will be removed. You will no longer update stock quantity, prices, and listing status of Listing via ERP."
                />
            )}

            {pendingUnmap && (
                <ConfirmArchiveModal
                    isOpen={!!pendingUnmap}
                    onCancel={() => setPendingUnmap(null)}
                    onConfirm={confirmUnmapMapping}
                    title="Confirm to unmap this Listing?"
                    description="Once this Listing is unmapped, it will be removed. You will no longer update stock quantity, prices, and listing status of it via ERP."
                />
            )}

            {pendingAction && (
                <ConfirmArchiveModal
                    isOpen={!!pendingAction}
                    onCancel={() => setPendingAction(null)}
                    onConfirm={confirmPendingAction}
                    title={ACTION_MODAL_CONTENT[pendingAction.action]?.title}
                    description={ACTION_MODAL_CONTENT[pendingAction.action]?.description}
                />
            )}

            {/* ─── Drawers ─── */}
            {drawer && (
                <>
                    {/* Only show listing-drawer overlay when stock sub-drawer and modals are NOT open */}
                    {!stockDrawerId && !skuDrawerOpen && !archivedModalOpen && !pendingUnmap && !pendingAction && (
                        <div className="drawer-overlay" onClick={() => { setDrawer(null); setDrawerMenuOpen(false); }} />
                    )}
                    <div className="entity-drawer" ref={drawerRef}>

                        {/* ══ LISTING-LEVEL DRAWER ══ */}
                        {drawer.type === 'listing' && (() => {
                            const { listing, group } = drawer;
                            const cp = group?.costPriceData;
                            const firstBase = listing.platforms.find(p => p.basePrice != null && p.basePrice !== 0);
                            return (
                                <>
                                    {/* Gray header bar */}
                                    <div className="drawer-header" style={{ background: '#F8F9FA',padding:"15px 20px" }}>
                                        <h3>Listing Info</h3>
                                        <button className="close-btn" onClick={() => setDrawer(null)}><X size={24} /></button>
                                    </div>

                                    <div className="main-drawer-inner" style={{padding:"0px"}}>
                                        {/* Product mini-header: image+name+badges left | Edit + ... right */}
                                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',padding:"15px 20px",background:'white'}}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                <img src={listing.avatar} alt="avatar" style={{ width:40 , height: 40, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }} />
                                                <div>
                                                    <div style={{ fontSize: 12, fontWeight: 400, color: '#212529' }}>{listing.name}</div>
                                                    <div style={{ fontSize: 10, color: '#6C757D', marginTop: 3 }}>{listing.variant}{listing.model_number ? ` | ${listing.model_number}` : ''}</div>
                                                    <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                                                        <StockTags condition={listing.condition} conditionType={listing.grading} setType={listing.setType} style={{color:'#212529', minWidth: 'unset', padding: '2px 5px', fontSize: '8px', lineHeight: 'normal', background: '#FFEB00',borderRadius:99,fontWeight:600 }} />
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                                                <button
                                                    style={{ background: '#212529', height: 34,width:67, padding: '6px 17px', border: 'none', color: '#fff', borderRadius: 4, cursor: 'pointer', fontSize: 16, fontWeight: 500, fontFamily: 'Montserrat' }}
                                                    onClick={() => { history.push('/listing-pricing/edit-listing', { listing, fromDrawer: { type: 'listing', listing, group: drawer.group } }); setDrawer(null); }}>
                                                    Edit
                                                </button>
                                                <div style={{ position: 'relative' }} ref={drawerMenuRef}>
                                                    <button
                                                        className={`plt-drawer-action-btn${drawerMenuOpen ? ' active' : ''}`}
                                                        onClick={() => setDrawerMenuOpen(o => !o)}
                                                    >
                                                        <MoreHorizontal size={18} color="#212529" />
                                                    </button>
                                                    {drawerMenuOpen && (
                                                        <div className="backdrop-cls" style={{ position: 'absolute', top: 40, right: 0, background: '#fff', boxShadow: '0px 1px 4px 0px #0000001A', zIndex: 1100, minWidth: 152, borderRadius: 8 }}>
                                                            <div className="btn-align-forall">
                                                                <div className="common-for-all">
                                                                    <button className="plt-dropdown-btn deactivate-cls" onClick={() => {
                                                                        setSelectedListingId(listing.id);
                                                                        setArchivedModalOpen(true);
                                                                        setDrawerMenuOpen(false);
                                                                    }}>Unmap All</button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Unmap All — right-aligned in red */}
                                        {/* <div style={{ textAlign: 'right', padding: '6px 20px 14px' }}>
                                            <button
                                                style={{ background: 'none', border: 'none', padding: 0, color: '#B02A37', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'Montserrat' }}
                                                onClick={() => { setSelectedListingId(listing.id); setArchivedModalOpen(true); }}>
                                                Unmap All
                                            </button>
                                        </div> */}

                                        {/* General / Additional tabs */}
                                        <div className="drawer-tabs" style={{ borderBottom: 'none', padding: '0 20px 10px', background: '#fff' }}>
                                            <button className={drawerTab === 'general' ? 'active' : ''} onClick={() => setDrawerTab('general')}>General</button>
                                            <button className={drawerTab === 'additional' ? 'active' : ''} onClick={() => setDrawerTab('additional')}>Additional</button>
                                        </div>

                                        {/* Basic Info card */}
                                        {drawerTab === 'general' && (
                                        <div style={{ padding: '20px',background:'white' }}>
                                            <div className="card-section" style={{ borderRadius: 12, boxShadow: '0px 4px 12px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                                                <div style={{ background: '#F8F9FA', padding: '15px 20px', fontSize: 14, fontWeight: 600, color: '#212529', borderBottom: '1px solid #DEE2E6',borderRadius:'12px 12px 0 0' }}>
                                                    Basic Info
                                                </div>
                                                <div style={{ background: '#fff', padding: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 20, columnGap: 20 }}>

                                                    {/* Stock — click image or name to open StockDrawer */}
                                                    <div>
                                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#212529', marginBottom: 6 }}>Stock</div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                            {/* Clickable image */}
                                                            <img
                                                                src={listing.avatar}
                                                                alt=""
                                                                style={{ width: 32, height: 32, borderRadius: 4, objectFit: 'cover', flexShrink: 0, marginTop: 2, cursor: 'pointer' }}
                                                                onClick={e => { e.stopPropagation(); if (listing.stockId) { setStockDrawerId(listing.stockId); setStockDrawerModelNumber(listing.model_number || null); } }}
                                                            />
                                                            <div>
                                                                {/* Clickable name + link icon */}
                                                                <a
                                                                    href="#!"
                                                                    onClick={e => { e.preventDefault(); e.stopPropagation(); if (listing.stockId) { setStockDrawerId(listing.stockId); setStockDrawerModelNumber(listing.model_number || null); } }}
                                                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: '#2C7BE5', textDecoration: 'underline' }}
                                                                >
                                                                    {listing.name}
                                                                    <SquareArrowOutUpRight size={13} strokeWidth={2.5} />
                                                                </a>
                                                                <div style={{ fontSize: 10, color: '#6C757D', marginTop: 3 }}>{listing.variant}{listing.model_number ? ` | ${listing.model_number}` : ''}</div>
                                                                <div style={{ display: 'flex', gap: 3, marginTop: 3 }}>
                                                                    <StockTags condition={listing.condition} conditionType={listing.grading} setType={listing.setType} style={{ minWidth: 'unset', padding: '2px 5px', fontSize: '8px', lineHeight: 'normal', background: '#FFEB00', borderRadius: '99px', color: '#212529', fontWeight: '600' }} />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* In Stock */}
                                                    <div>
                                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#212529', marginBottom: 6 }}>In Stock</div>
                                                        <div style={{ fontSize: 12, color: '#212529' }}>{group?.inStock ?? listing.inStock}</div>
                                                        {/* This listing's own commitment, not the stock row's — the sub-row
                                                            is one listing, so listing.committedQty is the figure that belongs
                                                            here even where In Stock above falls back to the group. */}
                                                        {listing.committedQty > 0 && (
                                                            <div style={COMMITTED_STYLE}>
                                                                Committed: {listing.committedQty}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Cost Price */}
                                                    <div>
                                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#212529', marginBottom: 6 }}>Cost Price</div>
                                                        <div style={{ fontSize: 12, color: '#212529' }}>
                                                            {cp ? (cp.is_uniform ? `SGD ${formatPrice(cp.min)}` : `SGD ${formatPrice(cp.min)} → SGD ${formatPrice(cp.max)}`) : <span style={{ color: '#ADB5BD' }}>-</span>}
                                                        </div>
                                                    </div>

                                                    {/* Base Price */}
                                                    <div>
                                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#212529', marginBottom: 6 }}>Base Price</div>
                                                        <div style={{ fontSize: 12, color: '#212529' }}>
                                                            {firstBase ? `${firstBase.basePriceCurrency} ${formatPrice(firstBase.basePrice)}` : <span style={{ color: '#ADB5BD' }}>-</span>}
                                                        </div>
                                                    </div>

                                                    {/* Created By */}
                                                    <div>
                                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#212529', marginBottom: 6 }}>Created By</div>
                                                        <div style={{ fontSize: 12, color: '#212529' }}>{listing.created_by || <span style={{ color: '#ADB5BD' }}>-</span>}</div>
                                                    </div>

                                                    {/* Created Time */}
                                                    <div>
                                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#212529', marginBottom: 6 }}>Created Time</div>
                                                        <div style={{ fontSize: 12, color: '#212529' }}>{listing.created_at ? formatDateTime(listing.created_at) : <span style={{ color: '#ADB5BD' }}>-</span>}</div>
                                                    </div>

                                                    {/* Last Modified By */}
                                                    <div>
                                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#212529', marginBottom: 6 }}>Last Modified By</div>
                                                        <div style={{ fontSize: 12, color: '#212529' }}>{listing.modified_by || <span style={{ color: '#ADB5BD' }}>-</span>}</div>
                                                    </div>

                                                    {/* Last Modified Time */}
                                                    <div>
                                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#212529', marginBottom: 6 }}>Last Modified Time</div>
                                                        <div style={{ fontSize: 12, color: '#212529' }}>{listing.modified_at ? formatDateTime(listing.modified_at) : <span style={{ color: '#ADB5BD' }}>-</span>}</div>
                                                    </div>

                                                </div>
                                            </div>
                                        </div>
                                        )}

                                        {drawerTab === 'additional' && (
                                            <div style={{ padding: '20px', background: 'white' }}>
                                                <div className="card-section" style={{ borderRadius: 12, boxShadow: '0px 4px 12px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                                                    <div style={{ background: '#F8F9FA', padding: '15px 20px', fontSize: 14, fontWeight: 600, color: '#212529', borderBottom: '1px solid #DEE2E6', borderRadius: '12px 12px 0 0' }}>
                                                        Notes
                                                    </div>
                                                    <div style={{ background: '#fff', padding: '15px 20px', borderRadius: '0 0 12px 12px' }}>
                                                        <p style={{ margin: 0, fontFamily: 'Montserrat', fontSize: 14, color: '#212529', lineHeight: '22px', whiteSpace: 'pre-line' }}>
                                                            {(listing.notes || '').trim() || <span style={{ color: '#ADB5BD' }}>-</span>}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </>
                            );
                        })()}

                        {/* ══ PLATFORM-LEVEL DRAWER ══ */}
                        {drawer.type === 'platform' && (() => {
                            // group is the stock row the drawer was opened from; it carries
                            // the stock-level committedQty that erpAvailableQty subtracts.
                            const { listing, platform: p, group } = drawer;
                            const cleanStr = v => (v && v !== 'Invalid ID') ? v : null;
                            const hasStatus = !!p?.lastSyncStatus;
                            const isFailed = p?.isFailed;
                            const isPending = p?.isPending;
                            return (
                                <>
                                    <div className="drawer-header" style={{ background: '#F8F9FA',padding:"15px 20px" }}>
                                        <h3>Listing Info</h3>
                                        <button className="close-btn" onClick={() => setDrawer(null)}><X size={24} /></button>
                                    </div>
                                    
                                    <div className="main-drawer-inner" style={{ padding: '0px',background:'white' }}>
                                    {/* White header: dot + platform name left | ... + X right */}
                                    <div style={{ background: '#fff', borderBottom: 'none',display:'flex',alignItems:'center',justifyContent:'space-between',padding:'15px 20px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <svg width="10" height="10" viewBox="0 0 8 8" fill="none" style={{ flexShrink: 0 }}>
                                                <circle cx="4" cy="4" r="3" fill={p?.dotColor || '#CED4DA'} />
                                            </svg>
                                            <h3 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: '#212529',fontFamily:'Montserrat' }}>{p?.name}</h3>
                                            {p?.isCampaign && (
                                                <span title={p?.lastErrorMessage || CAMPAIGN_TAG_TITLE} style={CAMPAIGN_TAG_STYLE_LG}>
                                                    Campaign
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <div style={{ position: 'relative' }} ref={drawerMenuRef}>
                                                <button
                                                    className={`plt-drawer-action-btn${drawerMenuOpen ? ' active' : ''}`}
                                                    onClick={() => setDrawerMenuOpen(o => !o)}
                                                >
                                                    <MoreHorizontal size={18} color="#212529" />
                                                </button>
                                                {drawerMenuOpen && (
                                                    <div className="backdrop-cls" style={{ position: 'absolute', top: 40, right: 0, background: '#fff', boxShadow: '0px 1px 4px 0px #0000001A', zIndex: 1100, minWidth: 152, borderRadius: 8 }}>
                                                        <div className="btn-align-forall">
                                                            {(() => {
                                                                const dPName = (p?.name || '').toLowerCase();
                                                                const dIsWoo = dPName.includes('woocommerce');
                                                                const dIsSupported = dPName.includes('lazada') || dPName.includes('shopee') || dPName.includes('tiktok') || dPName.includes('tik tok');
                                                                const dIsSoldOut = p?.stockSyncOption
                                                                    ? p.stockSyncOption === 'sync_erp'
                                                                    : (p?.platformStock == null ? true : Number(p.platformStock) > 0);
                                                                const dIsActing = stockActionLoading === p?.mappingId;
                                                                // Disable stock action for any non-success sync (failed or pending).
                                                                const dIsDisabled = dIsActing || p?.isFailed || p?.isPending;
                                                                
                                                                return (
                                                                    <>
                                                                        {dIsWoo && (
                                                                            <div className="common-for-all">
                                                                                <button
                                                                                    className="plt-dropdown-btn"
                                                                                    disabled={p?.isDelisted && p?.platformPrice == null}
                                                                                    style={{ opacity: (p?.isDelisted && p?.platformPrice == null) ? 0.4 : 1, cursor: (p?.isDelisted && p?.platformPrice == null) ? 'not-allowed' : 'pointer' }}
                                                                                    onClick={() => { if (!(p?.isDelisted && p?.platformPrice == null)) requestAction(listing, p, p?.isDelisted ? 'list' : 'delist'); }}
                                                                                >
                                                                                    {p?.isDelisted ? 'Relist' : 'Delist'}
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                        {dIsSupported && (
                                                                            <div className="common-for-all">
                                                                                <button
                                                                                    className="plt-dropdown-btn"
                                                                                    disabled={dIsDisabled}
                                                                                    style={{ opacity: dIsDisabled && !dIsActing ? 0.4 : 1, cursor: dIsDisabled ? 'not-allowed' : 'pointer' }}
                                                                                    onClick={() => { if (!dIsDisabled) requestAction(listing, p, dIsSoldOut ? 'sold_out' : 'restore'); }}
                                                                                >
                                                                                    {dIsActing ? 'Updating...' : dIsSoldOut ? 'Sold Out' : 'Restore Stock'}
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                        <div className="common-for-all">
                                                                            <button className="plt-dropdown-btn deactivate-cls" onClick={() => handleUnmapMapping(listing, p)}>Unmap</button>
                                                                        </div>
                                                                    </>
                                                                );
                                                            })()}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {/* General / Additional tabs */}
                                    <div className="drawer-tabs" style={{ borderBottom: 'none', padding: '0 20px 10px', background: '#fff' }}>
                                        <button className={drawerTab === 'general' ? 'active' : ''} onClick={() => setDrawerTab('general')}>General</button>
                                        <button className={drawerTab === 'additional' ? 'active' : ''} onClick={() => setDrawerTab('additional')}>Additional</button>
                                    </div>

                                    {drawerTab === 'general' && (
                                    <div style={{ padding: '20px',background:'white' }}>
                                        <div className="card-section" style={{ borderRadius: 12,boxShadow: '0px 4px 12px rgba(0,0,0,0.08)',  overflow: 'hidden' }}>
                                            <div style={{ background: '#F8F9FA', padding: '15px 20px', fontSize: 14, fontWeight: 600, color: '#212529', borderBottom: '1px solid #DEE2E6',borderRadius:'12px 12px 0 0' }}>
                                                Basic Info
                                            </div>
                                            <div style={{ background: '#fff', padding: '15px 20px', display: 'flex', flexDirection: 'column', gap: 20,borderRadius: '0 0 12px 12px' }}>

                                                {/* Product Info — full width */}
                                                <div>
                                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#212529', marginBottom: 6 }}>Product Info</div>
                                                    <div style={{ fontSize: 14, color: '#212529' }}>
                                                        {cleanStr(p?.productName) ?? <span style={{ color: '#ADB5BD' }}>-</span>}
                                                    </div>
                                                    {cleanStr(p?.platformProductId) && (
                                                        <div style={{ fontSize: 12, color: '#6C757D', marginTop: 3 }}>Product ID: {p.platformProductId}</div>
                                                    )}
                                                </div>

                                                {/* SKU Info — full width */}
                                                <div>
                                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#212529', marginBottom: 6 }}>SKU Info</div>
                                                    <div style={{ fontSize: 14, color: '#212529' }}>
                                                        {cleanStr(p?.skuName) ?? <span style={{ color: '#ADB5BD' }}>-</span>}
                                                    </div>
                                                    {cleanStr(p?.platformSkuId) && (
                                                        <div style={{ fontSize: 12, color: '#6C757D', marginTop: 3 }}>SKU ID: {p.platformSkuId}</div>
                                                    )}
                                                </div>

                                                {/* Platform Stock | Base Price */}
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                                    <div>
                                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#212529', marginBottom: 6 }}>Platform Stock</div>
                                                        <div style={{ fontSize: 14, color: isFailed ? p.textColor : '#212529' }}>
                                                            {isFailed || isPending || !hasStatus ? '-' : isStockInconsistent(p, erpAvailableQty(group, listing))
                                                                ? <StockInconsistencyBadge>{p?.platformStock}</StockInconsistencyBadge>
                                                                : p?.platformStock === 0
                                                                ? <span style={{ color: '#58151C', fontFamily: 'Montserrat', fontSize: 12, fontWeight: 400, lineHeight: 'normal' }}>Sold Out</span>
                                                                : (p?.platformStock ?? '-')}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#212529', marginBottom: 6 }}>Base Price</div>
                                                        <div style={{ fontSize: 14, color: '#212529' }}>
                                                            {p?.basePrice != null && p.basePrice !== 0 ? `${p.basePriceCurrency} ${formatPrice(p.basePrice)}` : <span style={{ color: '#ADB5BD' }}>-</span>}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Platform Price | Last Sync Time */}
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                                    <div>
                                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#212529', marginBottom: 6 }}>Platform Price</div>
                                                        <div style={{ fontSize: 14, color: isFailed ? p.textColor : '#212529' }}>
                                                            {isFailed || isPending || !hasStatus || !p?.platformPrice
                                                                ? '-'
                                                                : renderPlatformPriceValue(p)}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#212529', marginBottom: 6 }}>Last Sync Time</div>
                                                        <div style={{ fontSize: 14, color: isFailed ? p.textColor : '#212529' }}>
                                                            {isPending || !hasStatus ? '-' : (p?.lastSyncTime || '-')}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Campaign lock note: the backend's last_error_message says since
                                                    when the price is frozen and which ERP price is waiting. */}
                                                {p?.isCampaign && (
                                                    <div style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #CC9A06', background: '#FFF3CD', color: '#664D03', fontSize: 12, lineHeight: 1.5 }}>
                                                        {p?.lastErrorMessage || CAMPAIGN_TAG_TITLE}
                                                    </div>
                                                )}

                                                {/* Last Modified By | Last Modified Time */}
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                                    <div>
                                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#212529', marginBottom: 6 }}>Last Modified By</div>
                                                        <div style={{ fontSize: 14, color: '#212529' }}>{p?.platformModifiedBy || <span style={{ color: '#ADB5BD' }}>-</span>}</div>
                                                    </div>
                                                    <div>
                                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#212529', marginBottom: 6 }}>Last Modified Time</div>
                                                        <div style={{ fontSize: 14, color: '#212529' }}>{p?.platformModifiedAt ? formatDateTime(p.platformModifiedAt) : <span style={{ color: '#ADB5BD' }}>-</span>}</div>
                                                    </div>
                                                </div>

                                            </div>
                                        </div>
                                    </div>
                                    )}

                                    {drawerTab === 'additional' && (
                                        <div style={{ padding: '20px', background: 'white' }}>
                                            <div className="card-section" style={{ borderRadius: 12, boxShadow: '0px 4px 12px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                                                <div style={{ background: '#F8F9FA', padding: '15px 20px', fontSize: 14, fontWeight: 600, color: '#212529', borderBottom: '1px solid #DEE2E6', borderRadius: '12px 12px 0 0' }}>
                                                    Notes
                                                </div>
                                                <div style={{ background: '#fff', padding: '15px 20px', borderRadius: '0 0 12px 12px' }}>
                                                    <p style={{ margin: 0, fontFamily: 'Montserrat', fontSize: 14, color: '#212529', lineHeight: '22px', whiteSpace: 'pre-line' }}>
                                                        {(p?.notes || '').trim() || <span style={{ color: '#ADB5BD' }}>-</span>}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    </div>
                                </>
                            );
                        })()}

                    </div>
                </>
            )}

            {/* Branch balance popup — appears ABOVE the icon, centered, arrow at bottom */}
            {branchPopup && (() => {
                const { data, loading, rect } = branchPopup;
                // Center popup on icon's horizontal center; place bottom edge 10px above icon top
                const iconCenterX = rect.left + rect.width / 2;
                const popupBottom = window.innerHeight - rect.top + 10; // fixed: distance from viewport bottom

                // Chunk data into rows of 3 pairs each
                const COLS = 3;
                const rows = [];
                if (data) {
                    for (let i = 0; i < data.length; i += COLS) {
                        rows.push(data.slice(i, i + COLS));
                    }
                }

                return (
                    <div
                        ref={branchPopupRef}
                        className="plt-branch-popup"
                        style={{
                            bottom: popupBottom,
                            left: iconCenterX,
                            transform: 'translateX(-50%)',
                        }}
                        onMouseEnter={keepBranchPopupOpen}
                        onMouseLeave={closeBranchPopup}
                    >
                        {loading ? (
                            <div style={{ color: '#ADB5BD', fontSize: 12, fontFamily: 'Montserrat' }}>Loading...</div>
                        ) : !data || data.length === 0 ? (
                            <div style={{ color: '#ADB5BD', fontSize: 12, fontFamily: 'Montserrat' }}>No branch data available</div>
                        ) : (
                            <table className="plt-branch-table">
                                <tbody>
                                    {rows.map((rowItems, ri) => (
                                        <tr key={ri}>
                                            {rowItems.map((item, ci) => (
                                                <React.Fragment key={ci}>
                                                    <td className="plt-b-name">{item.branch}</td>
                                                    <td className={`plt-b-count${ci === rowItems.length - 1 ? ' last' : ''}`}>{item.count}</td>
                                                </React.Fragment>
                                            ))}
                                            {/* Fill empty cells if last row is short */}
                                            {rowItems.length < COLS && Array.from({ length: COLS - rowItems.length }).map((_, ei) => (
                                                <React.Fragment key={`e${ei}`}>
                                                    <td className="plt-b-name"></td>
                                                    <td className="plt-b-count"></td>
                                                </React.Fragment>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                );
            })()}

            {/* Stock sub-drawer — rendered AFTER listing drawer so it sits on top (same z-index, later DOM wins) */}
            {stockDrawerId && (
                <StockDrawer
                    stockId={stockDrawerId}
                    setDrawerOpen={() => setStockDrawerId(null)}
                    setSkuDrawerOpen={setSkuDrawerOpen}
                    setSelectedRow={setSelectedSkuRow}
                    fallbackModelNumber={stockDrawerModelNumber}
                    skuDrawerOpen={skuDrawerOpen}
                />
            )}

            {/* SKU sub-drawer — rendered last, highest stacking order */}
            {skuDrawerOpen && (
                <SkuInfoDrawer
                    selectedRow={selectedSkuRow}
                    setSelectedRow={setSelectedSkuRow}
                    setSkuDrawerOpen={setSkuDrawerOpen}
                />
            )}
        </div>
    );
}
